import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from 'react';
import {
  Sidebar,
  IconBar,
  MaterialIcon,
  BlockActionBar,
  Login,
  useFocusTrap,
  Toaster,
  useToasts,
  FileDropOverlay,
  ConfirmDialog,
  ContactsList,
  ShoppingDrawer,
  UtilitiesView,
  UtilityDetail,
  type HeadshotStudioContact,
  type IntegrationConnectProps,
  type IntegrationSettingsProps,
  AssistantPanel,
  setExternalOpener,
  setFileClipboardWriter,
  setFileSaver,
  setPlainClipboardReader,
  installNativePlainPasteBridge,
  setPlainPasteDefault,
  type SidebarNavItem,
  type SidebarItem,
  type LoginProvider,
  type AccountState,
  type GoogleAccountView,
  type IntegrationsState,
  type SubscriptionSectionState,
  type MovieEnricherSectionState,
  type RestaurantVerifierSectionState,
  type GeminiSectionState,
  type InstagramShareSectionState,
  type ClaveroSectionState,
  type BillPaySectionState,
  type BillPayConfigSave,
  type RedditMonitorSectionState,
  type DecisionLedgerSectionState,
  type ResurfaceSectionState,
  type ShipFromSectionState,
  type InitiativeMarker,
} from '@lapo/ui';
import { dailyJournalNoteId, rankCandidates, coerceIngestTriageConfig, DEFAULT_INGEST_TRIAGE_CONFIG, type IngestTriageConfig, isDailyJournalId, noteTypeFromId, PageType, INTEGRATIONS, findIntegration, isIntegrationEnabled, UTILITIES, toDateKey, normalizeUserContact, coerceWritingStyleProfile, coerceWorkingHours, coerceMenuLayout, defaultMenuLayout, reorderMenuLayout, type MenuLayout, type Utility, type Integration, type DocNodeJSON, type NoteRecord, type SyncStatus, type AccountIdentity, type UserContact, type WritingStyleProfile, type WorkingHours, type Category, type SubscriptionDigestConfig, type MovieEnricherConfig, type RestaurantVerifierConfig, type GeminiConfig, type InstagramShareConfig, type SlackConfig, type TodoistProject, type DecisionLedgerConfig, type BillPayConfig, type RedditConfig, type ProducerId, type ClaveroConfig, humanizeApiError } from '@lapo/core';
import { searchEntityIndex, fetchNotes, LocalNameIndex } from './linkCandidates.js';
import { fetchCategories } from './categories.js';
import { LinkResolver } from './linkResolver.js';
import { colorHex } from './loomColors.js';
import { effectiveStatus, statusLabel, lastActivityLabel } from './initiatives/status.js';
import { useSettings } from './useSettings.js';
import { useSystemHealth } from './health/useSystemHealth.js';
import { useToolbarConfig } from './useToolbarConfig.js';
import { useEngineConfig } from './useEngineConfig.js';
import { useCalendarPrefs } from './useCalendarPrefs.js';
import { useShipments } from './useShipments.js';
import { useContacts } from './useContacts.js';
import { useTodoistToday } from './useTodoistToday.js';
import { useIntegrationConfig } from './useIntegrationConfig.js';
import { useFinancial } from './useFinancial.js';
import { useIntegrationsHub } from './useIntegrationsHub.js';
import { usePodcasts } from './usePodcasts.js';
import { useVoice } from './useVoice.js';
import { useAgenda } from './useAgenda.js';
import { useAssistant } from './useAssistant.js';
import { useSignalStream } from './useSignalStream.js';
import { createAuthClient } from './auth/firebaseClient.js';
import { useAuth } from './auth/useAuth.js';
import {
  createApiClient,
  ApiError,
  type MeResponse,
  type AuthorizedAppDTO,
  type SlackWorkspaceView,
  type HealthEventDTO,
} from './auth/apiClient.js';
import { OAuthConsentScreen, parseConsentRequest } from './OAuthConsentScreen.js';
import { panelCacheKey, readPanelCache, writePanelCache } from './panelCache.js';
import { usePanelWidth } from './usePanelWidth.js';
import { useRightDrawer, RIGHT_VIEWS, SHIPPING_VIEW_INDEX, type RightViewId } from './useRightDrawer.js';
import { useHashRoute } from './useHashRoute.js';
import { useDayWindow } from './useDayWindow.js';
import { createWebPopupRedirect, createTauriDeepLinkRedirect, runLink, runTodoistLink, runTessetaLink, runSlackLink, runGmailModifyGrant } from './auth/oauthRedirect.js';
import { savePendingLink, clearPendingLink, resumePendingLink } from './auth/pendingLink.js';
import { runBrokeredGoogleLink, runBrokeredSlackLink, runBrokeredTodoistLink } from './auth/brokeredLink.js';
import { isTauri, isMobile, isIOS, isAndroid, isMacOS, macDownloadSuggestion } from './shell/shell.js';
import { installKeyboardViewportFix } from './shell/keyboardViewport.js';
import { runAutoUpdate } from './shell/autoUpdate.js';
import { useDesktopUpdater } from './shell/useDesktopUpdater.js';
import { initDeviceRegistration, unregisterThisDevice } from './deviceSync.js';
import { initShareTarget } from './shareTarget.js';
import type { AuthSession } from './auth/provider.js';
import { JournalDateNav } from './JournalDateNav.js';
import { LibraryView } from './LibraryView.js';
import { RedditMonitorView } from './RedditMonitorView.js';
import { ContactDetailView } from './ContactDetailView.js';
// Type-only — erased at build; the component itself is lazy-loaded below.
import type { CoachingModule } from './AICoachingView.js';
import { NoteIcon } from './NoteIcon.js';
import { TodoistPanel } from './TodoistPanel.js';
import { AgendaPanel } from './AgendaPanel.js';
import { DaySignals } from './DaySignals.js';
import { AutonomyPanel } from './Autonomy.js';
import { TeachProvider, GlobalTeachButton } from './teach/TeachContext.js';
import { initWebPush } from './webPush.js';
import { readFirebaseConfig } from './auth/firebaseConfig.js';
import { SearchModal } from './SearchModal.js';
import { LinkedReferences } from './LinkedReferences.js';
import {
  noteIndex,
  useNoteIndex,
  useToggleStar,
  titleFromDoc,
  mergeNotes,
  displayTitle,
  starredNotes,
  recentDocuments,
  dateFromJournalId,
  newDocumentId,
  type LocalNote,
} from './noteIndex.js';
import { LocalContentIndex } from './localContentIndex.js';
import { API_URL, TOKEN } from './config.js';
import { ErrorBoundary, LazyBoundary, lazyView } from './ErrorBoundary.js';
import { installGlobalErrorHandlers, setClientErrorSink } from './clientErrors.js';
import { setAuthTokenProvider, uploadFile } from './editorRuntime.js';
// Editor list/block commands via the light registry (PERF-001) — NOT the @lapo/ui barrel, which would
// drag the @tiptap/prosemirror runtime into the eager login bundle. The lazy editor chunk registers the
// real impls; every use here is guarded by a mounted editor, so the registry is always populated first.
import {
  indentListItem,
  outdentListItem,
  toggleNumbered,
  clearBlockSelection,
  indentBlockSelection,
  outdentBlockSelection,
  deleteBlockSelection,
  blockSelectionAllNumbered,
  copyBlockSelection,
  moveBlockSelection,
  canMoveBlockSelection,
  uploadAndInsert,
} from './editorCommands.js';
// Type-only import — erased at build, so it does NOT pull the lazy editors chunk into the main bundle.
import type { CandidateSource, BlockServices, EditorHandle } from './editors.js';

// Editors are code-split (audit F1b): the ProseMirror/Yjs/emoji stack loads only when an editor
// first renders (post-login), not in the login bundle. Local wrappers add a Suspense boundary so the
// existing <DayEditor/> / <DocEditor/> call sites stay unchanged.
const LazyDayEditor = lazyView(() => import('./editors.js').then((m) => ({ default: m.DayEditor })));
const LazyDocEditor = lazyView(() => import('./editors.js').then((m) => ({ default: m.DocEditor })));

// FIND-08: on-device content index for instant body search (flag-gated; server stays the fallback for
// notes not yet opened this session). Null when disabled so no indexing/persistence overhead.
const localContentIndex =
  import.meta.env.VITE_LOCAL_CONTENT_SEARCH === 'true' ? new LocalContentIndex() : null;
function DayEditor(props: ComponentProps<typeof LazyDayEditor>): ReactElement {
  return (
    <LazyBoundary>
      <LazyDayEditor {...props} />
    </LazyBoundary>
  );
}
function DocEditor(props: ComponentProps<typeof LazyDocEditor>): ReactElement {
  return (
    <LazyBoundary>
      <LazyDocEditor {...props} />
    </LazyBoundary>
  );
}

// Heavy view surfaces are code-split the same way (audit F1c): each mounts behind a `view.kind` /
// open-flag switch, so its code loads on first navigation, not in the boot bundle. The wrappers keep
// the call sites unchanged and add the app's standard null-fallback Suspense boundary (these are
// view-level surfaces, same convention as the lazy editors above).
//
// The @lapo/ui surfaces are imported by direct module path (see ui's `./components/*` subpath
// export): a dynamic import of the '@lapo/ui' BARREL would resolve to the same module graph the
// static barrel import already put in the main chunk, and nothing would split.
const LazySettingsModal = lazyView(() => import('@lapo/ui/components/SettingsModal').then((m) => ({ default: m.SettingsModal })));
// SYSTEM-HEALTH — admin-only detail panel; lazy so non-admins never fetch its chunk.
const LazyHealthPanel = lazyView(() => import('@lapo/ui/components/HealthPanel').then((m) => ({ default: m.HealthPanel })));
const LazyShipmentsView = lazyView(() => import('@lapo/ui/components/ShipmentsView').then((m) => ({ default: m.ShipmentsView })));
const LazyIntegrationsView = lazyView(() => import('@lapo/ui/components/IntegrationsView').then((m) => ({ default: m.IntegrationsView })));
const LazyIntegrationDetail = lazyView(() => import('@lapo/ui/components/IntegrationsView').then((m) => ({ default: m.IntegrationDetail })));
const LazySubscriptionReader = lazyView(() => import('@lapo/ui/components/SubscriptionReader').then((m) => ({ default: m.SubscriptionReader })));
const LazyFinancialDashboard = lazyView(() => import('@lapo/ui/components/FinancialDashboard').then((m) => ({ default: m.FinancialDashboard })));
const LazyFitnessView = lazyView(() => import('@lapo/ui/components/FitnessView').then((m) => ({ default: m.FitnessView })));
const LazyKudosView = lazyView(() => import('@lapo/ui/components/KudosView').then((m) => ({ default: m.KudosView })));
const LazyShoppingView = lazyView(() => import('@lapo/ui/components/ShoppingView').then((m) => ({ default: m.ShoppingView })));
const LazyHeadshotStudio = lazyView(() => import('@lapo/ui/components/HeadshotStudio').then((m) => ({ default: m.HeadshotStudio })));
// App-side view surfaces, same treatment.
const LazyDecisionLedgerView = lazyView(() => import('./DecisionLedgerView.js').then((m) => ({ default: m.DecisionLedgerView })));
const LazyBillsView = lazyView(() => import('./BillsView.js').then((m) => ({ default: m.BillsView })));
const LazyJobsView = lazyView(() => import('./JobsView.js').then((m) => ({ default: m.JobsView })));
const LazyLoomView = lazyView(() => import('./LoomView.js').then((m) => ({ default: m.LoomView })));
const LazyAICoachingView = lazyView(() => import('./AICoachingView.js').then((m) => ({ default: m.AICoachingView })));

function SettingsModal(props: ComponentProps<typeof LazySettingsModal>): ReactElement {
  return <LazyBoundary><LazySettingsModal {...props} /></LazyBoundary>;
}
function ShipmentsView(props: ComponentProps<typeof LazyShipmentsView>): ReactElement {
  return <LazyBoundary><LazyShipmentsView {...props} /></LazyBoundary>;
}
function IntegrationsView(props: ComponentProps<typeof LazyIntegrationsView>): ReactElement {
  return <LazyBoundary><LazyIntegrationsView {...props} /></LazyBoundary>;
}
function IntegrationDetail(props: ComponentProps<typeof LazyIntegrationDetail>): ReactElement {
  return <LazyBoundary><LazyIntegrationDetail {...props} /></LazyBoundary>;
}
function SubscriptionReader(props: ComponentProps<typeof LazySubscriptionReader>): ReactElement {
  return <LazyBoundary><LazySubscriptionReader {...props} /></LazyBoundary>;
}
function FinancialDashboard(props: ComponentProps<typeof LazyFinancialDashboard>): ReactElement {
  return <LazyBoundary><LazyFinancialDashboard {...props} /></LazyBoundary>;
}
function FitnessView(props: ComponentProps<typeof LazyFitnessView>): ReactElement {
  return <LazyBoundary><LazyFitnessView {...props} /></LazyBoundary>;
}
function KudosView(props: ComponentProps<typeof LazyKudosView>): ReactElement {
  return <LazyBoundary><LazyKudosView {...props} /></LazyBoundary>;
}
function ShoppingView(props: ComponentProps<typeof LazyShoppingView>): ReactElement {
  return <LazyBoundary><LazyShoppingView {...props} /></LazyBoundary>;
}
function HeadshotStudio(props: ComponentProps<typeof LazyHeadshotStudio>): ReactElement {
  return <LazyBoundary><LazyHeadshotStudio {...props} /></LazyBoundary>;
}
function DecisionLedgerView(props: ComponentProps<typeof LazyDecisionLedgerView>): ReactElement {
  return <LazyBoundary><LazyDecisionLedgerView {...props} /></LazyBoundary>;
}
function BillsView(props: ComponentProps<typeof LazyBillsView>): ReactElement {
  return <LazyBoundary><LazyBillsView {...props} /></LazyBoundary>;
}
function JobsView(props: ComponentProps<typeof LazyJobsView>): ReactElement {
  return <LazyBoundary><LazyJobsView {...props} /></LazyBoundary>;
}
function LoomView(props: ComponentProps<typeof LazyLoomView>): ReactElement {
  return <LazyBoundary><LazyLoomView {...props} /></LazyBoundary>;
}
function AICoachingView(props: ComponentProps<typeof LazyAICoachingView>): ReactElement {
  return <LazyBoundary><LazyAICoachingView {...props} /></LazyBoundary>;
}

/**
 * Delete all locally-cached note data. Each note's Yjs doc persists to an IndexedDB database named
 * `lapo-<noteId>` (see the editor pane below). Signing out wipes these so local state is genuinely
 * cleared, WITHOUT touching server data. Best-effort: `indexedDB.databases()` is unavailable in some
 * browsers (e.g. Firefox), in which case nothing is enumerated.
 */
async function clearLocalData(): Promise<void> {
  // The localStorage note catalog (titles/recents/favorites) and the drawer-panel caches (Todoist
  // Today, Shipments) — clear them too, else stale data persists across accounts on this device.
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('lapo-note-index') || key.startsWith('lapo-panel-')) localStorage.removeItem(key);
    }
  } catch {
    /* storage blocked */
  }
  try {
    const dbs = (await indexedDB.databases?.()) ?? [];
    await Promise.all(
      dbs
        .map((d) => d.name)
        .filter((name): name is string => !!name && name.startsWith('lapo-'))
        .map(
          (name) =>
            new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(name);
              req.onsuccess = req.onerror = req.onblocked = () => resolve();
            }),
        ),
    );
  } catch {
    /* best-effort local clear */
  }
}

/**
 * Delete ONE note's local Yjs cache (its `lapo-<noteId>` IndexedDB database) — used when discarding a
 * blank page, the single-note counterpart to {@link clearLocalData}. Best-effort and idempotent; a
 * blocked/absent database still resolves so the caller never hangs on cleanup.
 */
async function deleteNoteCache(noteId: string): Promise<void> {
  try {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(`lapo-${noteId}`);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Lapo web frontend — Logseq-style. Left sidebar (graph/nav/favorites/recents), a headerless
 * icon toolbar, and a CONTINUOUS journal that stacks each day newest-first from an anchor date
 * (default today; the date navigator re-anchors it). Each day is its own note/file (its own Y.Doc
 * `journal-YYYY-MM-DD`) with local-first IndexedDB persistence and optional websocket sync
 * (architecture §3, §4).
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function ordinal(n: number): string {
  if (n >= 11 && n <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}
function dayTitle(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${ordinal(d.getDate())}, ${d.getFullYear()}`;
}
/** Local midnight of the current day — the journal's live "today" anchor. */
function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
/** Whether two Dates fall on the same local calendar day. */
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
/** Days the journal appends per infinite-scroll page (and its initial window size). */
const DAYS_PER_LOAD = 10;

/** How often to re-check whether the wall-clock day has rolled over (keeps the journal on "today"). */
const DAY_ROLLOVER_POLL_MS = 60 * 1000;

/** Client-local YYYY-MM-DD for a Date, so "today" honors the viewer's timezone (not the server's). */
function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** `dateKey` shifted by `delta` days, staying in YYYY-MM-DD. */
function shiftDateKey(dateKey: string, delta: number): string {
  const [y, m, d] = dateKey.slice(0, 10).split('-').map(Number);
  const base = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  base.setDate(base.getDate() + delta);
  return localDateKey(base);
}
/** `count` days ending at `anchor` (inclusive), newest-first — so `anchor` is the top entry. */
function buildDays(count: number, anchor: Date): Array<{ noteId: string; title: string; isToday: boolean }> {
  const out: Array<{ noteId: string; title: string; isToday: boolean }> = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() - i);
    const isToday =
      d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
    out.push({ noteId: dailyJournalNoteId(d), title: dayTitle(d), isToday });
  }
  return out;
}


// Cached at module scope (audit F3): nav icons are static JSX — re-minting them each render gave the
// memoized Sidebar fresh element identities and defeated its bailout.
const navIconCache = new Map<string, ReactElement>();
const navIcon = (name: string): ReactElement => {
  let el = navIconCache.get(name);
  if (!el) {
    el = <MaterialIcon name={name} size={18} />;
    navIconCache.set(name, el);
  }
  return el;
};

/** The main pane's current surface. */
type View =
  | { kind: 'journals' }
  | { kind: 'library' }
  | { kind: 'reddit' }
  | { kind: 'contacts' }
  | { kind: 'contact'; contactId: string }
  | { kind: 'initiatives'; openName?: string }
  | { kind: 'integrations' }
  | { kind: 'integration'; id: string }
  | { kind: 'utilities' }
  | { kind: 'utility'; id: string }
  | { kind: 'subscription' }
  | { kind: 'finances' }
  | { kind: 'fitness' }
  | { kind: 'kudos' }
  | { kind: 'shopping' }
  | { kind: 'bills'; billId?: string }
  | { kind: 'jobs'; jobId?: string }
  | { kind: 'decisions'; openId?: string }
  | { kind: 'ai-coaching'; module: CoachingModule }
  | { kind: 'doc'; noteId: string; initialTitle?: string; scrollToBlockId?: string };

/** A typed quarter suffix on a `+` picker query, e.g. `Fitness/26q1` → `2026-Q1` (LOOM D-13), or null. */
const QUARTER_SUFFIX_RE = /\/(\d{2})[qQ]([1-4])$/;
function parseQuarterSuffix(query: string): string | null {
  const m = QUARTER_SUFFIX_RE.exec(query.trim());
  if (!m) return null;
  return `20${m[1]}-Q${m[2]}`;
}
/** The query with any trailing `/26q1` quarter suffix removed, for candidate matching. */
function stripQuarterSuffix(query: string): string {
  return query.replace(QUARTER_SUFFIX_RE, '');
}

/** HONE — map the active view to the learning producer its global Teach note should address. */
function producerForView(kind: View['kind']): { producerId: ProducerId; label: string } {
  switch (kind) {
    case 'decisions':
      return { producerId: 'ledger', label: 'the Decisions ledger' };
    case 'finances':
      return { producerId: 'finance', label: 'Finances' };
    case 'subscription':
      return { producerId: 'engines', label: 'the Subscription digest' };
    default:
      return { producerId: 'system', label: 'Lapo' };
  }
}

/**
 * URL-hash routing (so navigation is linkable and survives reload / back-forward). The hash mirrors
 * the active surface: `#/journal/YYYY-MM-DD` (the journal anchored at that day), `#/library`,
 * `#/contacts`, `#/doc/<noteId>`. It's a reflection of `view` (+ `anchorDate`), written with
 * `replaceState` so it never spams browser history; back/forward is handled via `hashchange`.
 */
type Route =
  | { kind: 'journals'; date: Date | null }
  | { kind: 'library' }
  | { kind: 'reddit' }
  | { kind: 'contacts' }
  | { kind: 'contact'; contactId: string }
  | { kind: 'initiatives'; openName?: string }
  | { kind: 'integrations' }
  | { kind: 'integration'; id: string }
  | { kind: 'utilities' }
  | { kind: 'utility'; id: string }
  | { kind: 'subscription' }
  | { kind: 'finances' }
  | { kind: 'fitness' }
  | { kind: 'kudos' }
  | { kind: 'shopping' }
  | { kind: 'bills'; billId?: string }
  | { kind: 'jobs'; jobId?: string }
  | { kind: 'decisions'; openId?: string }
  | { kind: 'ai-coaching'; module: CoachingModule }
  | { kind: 'doc'; noteId: string };

/** Parse `window.location.hash` into a Route, or null when it's empty/unrecognized. */
function parseHash(hash: string): Route | null {
  const h = hash.replace(/^#\/?/, '').trim();
  if (h === '' || h === 'journal' || h === 'journals') return { kind: 'journals', date: null };
  const parts = h.split('/');
  if (parts[0] === 'journal' && parts[1]) return { kind: 'journals', date: dateFromJournalId(`journal-${parts[1]}`) };
  if (parts[0] === 'library' || parts[0] === 'pages') return { kind: 'library' };
  if (parts[0] === 'reddit') return { kind: 'reddit' };
  if (parts[0] === 'contacts' && parts[1]) return { kind: 'contact', contactId: decodeURIComponent(parts.slice(1).join('/')) };
  if (parts[0] === 'contacts') return { kind: 'contacts' };
  if (parts[0] === 'initiatives' && parts[1]) return { kind: 'initiatives', openName: decodeURIComponent(parts.slice(1).join('/')) };
  if (parts[0] === 'initiatives') return { kind: 'initiatives' };
  if (parts[0] === 'integrations' && parts[1]) return { kind: 'integration', id: decodeURIComponent(parts[1]) };
  if (parts[0] === 'integrations') return { kind: 'integrations' };
  if (parts[0] === 'utilities' && parts[1]) return { kind: 'utility', id: decodeURIComponent(parts[1]) };
  if (parts[0] === 'utilities') return { kind: 'utilities' };
  if (parts[0] === 'subscription') return { kind: 'subscription' };
  if (parts[0] === 'finances') return { kind: 'finances' };
  if (parts[0] === 'fitness') return { kind: 'fitness' };
  if (parts[0] === 'kudos') return { kind: 'kudos' };
  if (parts[0] === 'shopping') return { kind: 'shopping' };
  if (parts[0] === 'bills' && parts[1]) return { kind: 'bills', billId: decodeURIComponent(parts[1]) };
  if (parts[0] === 'bills') return { kind: 'bills' };
  if (parts[0] === 'jobs' && parts[1]) return { kind: 'jobs', jobId: decodeURIComponent(parts[1]) };
  if (parts[0] === 'jobs') return { kind: 'jobs' };
  if (parts[0] === 'decisions' && parts[1]) return { kind: 'decisions', openId: decodeURIComponent(parts[1]) };
  if (parts[0] === 'decisions') return { kind: 'decisions' };
  if (parts[0] === 'ai-coaching') return { kind: 'ai-coaching', module: coachingModuleFrom(parts[1]) };
  if (parts[0] === 'doc' && parts[1]) return { kind: 'doc', noteId: decodeURIComponent(parts.slice(1).join('/')) };
  return null;
}

const COACHING_MODULES = new Set<CoachingModule>([
  'overview',
  'review',
  'guidance',
  'memory',
  'voice',
  'autonomy',
  'decisions',
  'initiatives',
  'digests',
  'finances',
  'signals',
]);

/** Coerce a hash segment into a valid AI Coaching module (defaults to the overview). */
function coachingModuleFrom(seg: string | undefined): CoachingModule {
  return seg && COACHING_MODULES.has(seg as CoachingModule) ? (seg as CoachingModule) : 'overview';
}

/** Serialize the current surface into a canonical hash (round-trips through parseHash). */
function viewToHash(view: View, anchorDate: Date): string {
  switch (view.kind) {
    case 'library':
      return '#/library';
    case 'reddit':
      return '#/reddit';
    case 'contacts':
      return '#/contacts';
    case 'contact':
      return `#/contacts/${encodeURIComponent(view.contactId)}`;
    case 'initiatives':
      return view.openName ? `#/initiatives/${encodeURIComponent(view.openName)}` : '#/initiatives';
    case 'integrations':
      return '#/integrations';
    case 'integration':
      return `#/integrations/${encodeURIComponent(view.id)}`;
    case 'utilities':
      return '#/utilities';
    case 'utility':
      return `#/utilities/${encodeURIComponent(view.id)}`;
    case 'subscription':
      return '#/subscription';
    case 'finances':
      return '#/finances';
    case 'fitness':
      return '#/fitness';
    case 'kudos':
      return '#/kudos';
    case 'shopping':
      return '#/shopping';
    case 'bills':
      return view.billId ? `#/bills/${encodeURIComponent(view.billId)}` : '#/bills';
    case 'jobs':
      return view.jobId ? `#/jobs/${encodeURIComponent(view.jobId)}` : '#/jobs';
    case 'decisions':
      return view.openId ? `#/decisions/${encodeURIComponent(view.openId)}` : '#/decisions';
    case 'ai-coaching':
      return `#/ai-coaching/${view.module}`;
    case 'doc':
      return `#/doc/${encodeURIComponent(view.noteId)}`;
    case 'journals':
    default:
      // The live "today" view stays a bare `#/journal` so a reload the next morning resolves to the
      // NEW today rather than pinning yesterday's date. Only a deliberately paged-back day is dated.
      return isSameDay(anchorDate, new Date())
        ? '#/journal'
        : `#/journal/${dailyJournalNoteId(anchorDate).replace('journal-', '')}`;
  }
}

// Exported for unit coverage — these routing/date helpers are pure (see App.helpers.test.ts). Kept in
// this module (rather than extracted) so App.tsx's own logic is exercised directly.
export {
  ordinal,
  dayTitle,
  startOfToday,
  isSameDay,
  localDateKey,
  shiftDateKey,
  buildDays,
  parseQuarterSuffix,
  stripQuarterSuffix,
  producerForView,
  parseHash,
  coachingModuleFrom,
  viewToHash,
};
export type { View, Route };

export function App(): ReactElement {
  // Starts offline; a sync provider (created only when signed in) drives it connecting → synced.
  const [status, setStatus] = useState<SyncStatus>('offline');
  // SYSTEM-HEALTH — admin gate (from /me) + the detail-panel open flag. Non-admins never see either.
  const [isAdmin, setIsAdmin] = useState(false);
  const [healthPanelOpen, setHealthPanelOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The (lazy) Settings modal mounts only once first opened, so its chunk isn't fetched at boot.
  // After that it STAYS mounted — it returns null while closed — preserving its internal state
  // across open/close exactly as the always-mounted static import did.
  const [settingsEverOpened, setSettingsEverOpened] = useState(false);
  useEffect(() => {
    if (settingsOpen) setSettingsEverOpened(true);
  }, [settingsOpen]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Right-hand drawer (the far-right panel icon): open state + which panel + width + all the ways it
  // changes, plus the mobile Back-gesture effects — all live in useRightDrawer (audit ARCH-006).
  const {
    rightOpen,
    rightViewIndex,
    rightWidth,
    startRightResize,
    resetRightWidth,
    openRightPanel,
    setRightViewIndex,
    closeRight,
    toggleRight,
    cycleRightPanel,
    panelVisible,
  } = useRightDrawer();
  // Desktop sidebar collapse (the toolbar's panel icon); independent of the mobile drawer.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Draggable sidebar width (desktop). Persisted so it survives reloads; clamped to a sane range.
  // (The right drawer's width lives in useRightDrawer — audit F5/F6 unified the two resize blocks.)
  const [sidebarWidth, startSidebarResize, resetSidebarWidth] = usePanelWidth({
    storageKey: 'lapo-sidebar-width',
    min: 200,
    max: 480,
    initial: 256,
  });
  // The surface (and journal date) to boot into, read once from the URL hash so a reloaded/shared link
  // restores where you were. Computed once (ref) so re-renders don't reparse.
  const bootRoute = useRef<Route | null>(parseHash(typeof window !== 'undefined' ? window.location.hash : '')).current;
  // Third-party OAuth authorization request (`#/oauth/authorize?…`). Captured here, at first render,
  // BEFORE the hash-sync effect can rewrite the URL — so the consent screen still has the params even
  // after the app normalizes the hash. When set, we render the consent screen instead of the app shell.
  const consentRequest = useRef(
    typeof window !== 'undefined'
      ? // CONDUIT (DL-13): standards-shaped OAuth clients (MCP/Claude Code) carry the params in
        // location.search with a bare `#/oauth/authorize` fragment — pass both URL halves.
        parseConsentRequest(window.location.hash, window.location.search)
      : null,
  ).current;
  // The journal's top date. The date navigator re-anchors here and the view jumps to it.
  const [anchorDate, setAnchorDate] = useState<Date>(() => {
    const d = bootRoute?.kind === 'journals' && bootRoute.date ? new Date(bootRoute.date) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  // Whether the anchor is tracking the live "today" (vs. deliberately paged back to a past day). Booted
  // true unless the URL pinned a specific past date. Drives the midnight rollover below so a tab left
  // open overnight rolls to the new day, while a user reading an old day is left where they are.
  const trackingTodayRef = useRef<boolean>(!(bootRoute?.kind === 'journals' && bootRoute.date && !isSameDay(new Date(bootRoute.date), new Date())));
  // Live anchor for the rollover check, so its effect can stay bound once (no re-arm per date change).
  const anchorDateRef = useRef(anchorDate);
  anchorDateRef.current = anchorDate;
  const [authBusy, setAuthBusy] = useState(false);
  // Which surface the main pane shows. Journals (the continuous day stack), the Library list, or a
  // single document. The sidebar nav, favorites/recents, search, and Create all drive this. Booted
  // from the URL hash (bootRoute) so a shared/reloaded link lands on the same surface.
  const [view, setView] = useState<View>(() => {
    if (bootRoute?.kind === 'doc') return { kind: 'doc', noteId: bootRoute.noteId };
    if (bootRoute?.kind === 'library') return { kind: 'library' };
    if (bootRoute?.kind === 'reddit') return { kind: 'reddit' };
    if (bootRoute?.kind === 'contacts') return { kind: 'contacts' };
    if (bootRoute?.kind === 'contact') return { kind: 'contact', contactId: bootRoute.contactId };
    if (bootRoute?.kind === 'integrations') return { kind: 'integrations' };
    if (bootRoute?.kind === 'integration') return { kind: 'integration', id: bootRoute.id };
    if (bootRoute?.kind === 'utilities') return { kind: 'utilities' };
    if (bootRoute?.kind === 'utility') return { kind: 'utility', id: bootRoute.id };
    if (bootRoute?.kind === 'initiatives') return { kind: 'initiatives', openName: bootRoute.openName };
    if (bootRoute?.kind === 'decisions') return { kind: 'decisions', openId: bootRoute.openId };
    if (bootRoute?.kind === 'bills') return { kind: 'bills', billId: bootRoute.billId };
    if (bootRoute?.kind === 'jobs') return { kind: 'jobs', jobId: bootRoute.jobId };
    if (bootRoute?.kind === 'ai-coaching') return { kind: 'ai-coaching', module: bootRoute.module };
    return { kind: 'journals' };
  });
  // Global search palette (toolbar search icon / Cmd-K). A ref lets the popstate listener read the live
  // open state without re-binding (mirrors rightOpenRef).
  const [searchOpen, setSearchOpen] = useState(false);
  const searchOpenRef = useRef(searchOpen);
  searchOpenRef.current = searchOpen;
  // Pending destructive confirmation (themed dialog instead of window.confirm).
  const [confirm, setConfirm] = useState<{ title: string; body?: string; confirmLabel?: string; onConfirm: () => void } | null>(null);
  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();
  const { settings, update: updateSettings } = useSettings();
  // Latest settings, readable from callbacks/effects without adding `settings` to their deps.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  // Desktop shell only — the updater plugin is registered under #[cfg(desktop)]. Drives the manual
  // "Check for updates" control in Settings → General (state passed straight through to SettingsModal).
  const desktopEnabled = isTauri() && !isMobile();
  const desktopUpdater = useDesktopUpdater(desktopEnabled);
  // Desktop self-update: on the macOS shell, check for a newer release once on mount and install it
  // silently in the background. On success we surface a sticky toast; the update applies on next launch
  // either way. Every failure (offline, 404, signature mismatch) is swallowed in runAutoUpdate — never fatal.
  useEffect(() => {
    const isDesktop = isTauri() && !isMobile();
    if (!isDesktop) return;
    void runAutoUpdate({
      isDesktop,
      log: (m) => console.info('[autoupdate]', m),
      onReady: (v) =>
        pushToast({
          tone: 'success',
          title: 'Update ready',
          message: `Lapo ${v} will apply next launch.`,
          duration: 0, // sticky — the user dismisses it (or just quits and relaunches)
        }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // How many days the continuous journal renders below the anchor. Infinite scroll grows this by a
  // page (DAYS_PER_LOAD) each time the reader nears the bottom; re-anchoring resets it.
  const [dayCount, setDayCount] = useState(DAYS_PER_LOAD);
  const days = useMemo(() => buildDays(dayCount, anchorDate), [anchorDate, dayCount]);
  // Journal day-stack windowing (audit F6): day sections far outside the viewport unmount their
  // editor subtree (editor + Y.Doc + IndexedDB + sync provider all tear down; DaySignals goes with
  // them) and render a height-preserving placeholder instead, so paging back through the journal
  // doesn't accumulate dozens of live CRDT stacks. Today + the anchor day always stay mounted.
  // `todayNoteId` is a fresh string each render but memo deps compare string VALUES, so the memo is
  // stable within a day and rolls over at midnight.
  const todayNoteId = dailyJournalNoteId(new Date());
  const dayWindowAlwaysLive = useMemo(() => {
    const anchorId = dailyJournalNoteId(anchorDate);
    return anchorId === todayNoteId ? [anchorId] : [anchorId, todayNoteId];
  }, [anchorDate, todayNoteId]);
  const { section: dayWindowSection, onHydrated: onDayHydrated } = useDayWindow(dayWindowAlwaysLive);
  const mainRef = useRef<HTMLElement | null>(null);
  // Scroll memory for the Integrations hub: entering a module resets the module page to the top; returning
  // to the list restores where it was (the list + modules share the one `.lapo-main-scroll` container).
  const integrationsScrollRef = useRef(0);
  const prevViewRef = useRef(view);
  // Guards the scroll handler so each near-bottom pass loads exactly one page, not one per event;
  // cleared once the freshly appended days have rendered (the effect on days.length below).
  const loadingMoreRef = useRef(false);
  // The scroll handler bound to <main>, kept so the ref callback can detach it on unmount.
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  // Read inside the (stable) scroll handler so it only loads while the journal is the active surface,
  // without rebinding the listener on every view change.
  const viewKindRef = useRef(view.kind);
  viewKindRef.current = view.kind;
  // Infinite scroll, attached via a callback ref rather than an effect. On first load the app renders
  // the login/loading gate — <main> isn't mounted — so an effect reading `mainRef.current` would see
  // null and, because `view.kind` never changes, never re-run: the listener would attach only after
  // navigating away from the journal and back (the exact symptom seen). A ref callback fires on the
  // real mount/unmount of <main>, so infinite scroll works on the very first journal view.
  const setMainRef = useCallback((node: HTMLElement | null) => {
    if (mainRef.current && scrollHandlerRef.current) {
      mainRef.current.removeEventListener('scroll', scrollHandlerRef.current);
    }
    mainRef.current = node;
    scrollHandlerRef.current = null;
    if (node) {
      const onScroll = (): void => {
        if (loadingMoreRef.current || viewKindRef.current !== 'journals') return;
        // Near the bottom → append another page of older days. A short viewport that doesn't overflow
        // simply won't scroll, so this never runs away loading empty days back through time.
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 800) {
          loadingMoreRef.current = true;
          setDayCount((c) => c + DAYS_PER_LOAD);
        }
      };
      scrollHandlerRef.current = onScroll;
      node.addEventListener('scroll', onScroll, { passive: true });
    }
  }, []);
  // Integrations scroll handling: a module page opens at the top; returning to the list restores its
  // scroll. Runs before paint (useLayoutEffect) so there's no visible jump. Only touches the shared
  // scroll container for Integrations transitions — the journal's own scrolling is left untouched.
  useLayoutEffect(() => {
    const el = mainRef.current;
    const prev = prevViewRef.current;
    prevViewRef.current = view;
    if (!el) return;
    if (view.kind === 'integration' && prev.kind === 'integrations') {
      el.scrollTop = 0; // list → module: start the module page at the top
    } else if (view.kind === 'integrations' && prev.kind === 'integration') {
      el.scrollTop = integrationsScrollRef.current; // module → list: keep the list where it was
    }
  }, [view]);

  // Re-anchor the journal to a chosen date, close the mobile drawer, and scroll back to the top entry.
  const jumpToDate = useCallback((d: Date) => {
    // Picking today (the date navigator's default / the Journals nav) resumes live tracking so the
    // anchor keeps rolling over at midnight; picking a past day pins it there until the user returns.
    trackingTodayRef.current = isSameDay(d, new Date());
    setAnchorDate(d);
    setDayCount(DAYS_PER_LOAD);
    setSidebarOpen(false);
    mainRef.current?.scrollTo({ top: 0 });
  }, []);
  // Keep the journal on the live "today": when the wall clock rolls to a new day (a tab left open
  // overnight is the common case), re-anchor to the new day so the morning shows — and creates — that
  // day's entry without a manual date pick. Only fires while tracking today; a user paged back to a
  // past day is left alone. Polls on a minute tick and on tab refocus (covers a sleeping timer).
  useEffect(() => {
    const maybeRoll = (): void => {
      if (!trackingTodayRef.current) return;
      const today = startOfToday();
      if (anchorDateRef.current.getTime() === today.getTime()) return;
      setAnchorDate(today);
      setDayCount(DAYS_PER_LOAD);
      mainRef.current?.scrollTo({ top: 0 });
    };
    const timer = window.setInterval(maybeRoll, DAY_ROLLOVER_POLL_MS);
    const onVisible = (): void => { if (document.visibilityState === 'visible') maybeRoll(); };
    window.addEventListener('focus', maybeRoll);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', maybeRoll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  // Newly appended days have rendered (or the anchor reset) — re-arm the scroll loader.
  useEffect(() => {
    loadingMoreRef.current = false;
  }, [days.length]);

  // URL-hash sync (write + read) — mirror the active surface into the hash and re-navigate on
  // Back/Forward or a manual hash edit — lives in useHashRoute (audit ARCH-006). Extracted verbatim;
  // linkable URLs, history push/replace, and the consent-in-flight skip are byte-for-byte preserved.
  useHashRoute({ view, anchorDate, setView, jumpToDate, consentRequest });

  // Mobile: the right drawer closes on the browser Back button/gesture (the same marker trick) — that
  // effect pair now lives in useRightDrawer (audit ARCH-006).

  // Mobile: the search palette closes on the browser Back button/gesture, same marker trick as the
  // right drawer. This effect sits AFTER the hash-write effect on purpose: when a search result is
  // picked, openNote both closes search and navigates, so that effect has already pushed the doc entry
  // by the time we run — our marker is no longer on top, the state guard fails, and we skip the back()
  // (the doc push consumed the marker). Closing via scrim/Esc leaves the marker on top, so we pop it.
  const searchPushedRef = useRef(false);
  useEffect(() => {
    const isNarrowViewport = window.matchMedia('(max-width: 768px)').matches;
    if (searchOpen && isNarrowViewport && !searchPushedRef.current) {
      searchPushedRef.current = true;
      window.history.pushState({ lapoModal: 'search' }, '', window.location.hash);
    } else if (!searchOpen && searchPushedRef.current) {
      searchPushedRef.current = false;
      if ((window.history.state as { lapoModal?: string } | null)?.lapoModal === 'search') window.history.back();
    }
  }, [searchOpen]);
  useEffect(() => {
    const onPop = (): void => {
      if (searchOpenRef.current) {
        searchPushedRef.current = false; // Back consumed our marker entry
        setSearchOpen(false);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Identity (IMPL-03): Firebase login when configured, else local-only. The session yields the
  // Bearer token the API + sync layers verify. We mirror it into a ref so the stable api/sync token
  // getters always read the latest session without re-subscribing or rebuilding the client.
  const authClient = useMemo(() => createAuthClient(), []);
  const { session, configured, signIn, signOut } = useAuth(authClient);
  const sessionRef = useRef<AuthSession | null>(null);
  sessionRef.current = session;
  const getSyncToken = useCallback(async (): Promise<string> => {
    const s = sessionRef.current;
    return s ? s.getIdToken() : TOKEN;
  }, []);
  // Point the module-level file uploader / signed-URL resolver at the live token (else images 401).
  useEffect(() => {
    setAuthTokenProvider(async () => (sessionRef.current ? sessionRef.current.getIdToken() : null));
  }, []);
  // On the native shell, open external links / downloaded files in the system browser (an Android
  // WebView can't reliably `window.open`). No-op on the web (keeps the default tab behavior).
  useEffect(() => {
    if (!isTauri()) return;
    void import('@tauri-apps/plugin-opener').then((m) => setExternalOpener((url) => void m.openUrl(url)));
    // Back the file affordances with native commands: copy real file bytes to the OS clipboard and save
    // to Downloads (WKWebView can do neither from the webview). The ui defaults stay for the web build.
    void import('@tauri-apps/api/core').then(({ invoke }) => {
      setFileClipboardWriter(async (t) => {
        await invoke('copy_file_to_clipboard', { url: t.url, filename: t.filename });
      });
      setFileSaver(async (t) => {
        await invoke('save_file', { url: t.url, filename: t.filename });
      });
      // Ctrl/Cmd-Shift-V "paste without formatting" reads the clipboard natively: the desktop WKWebView's
      // navigator.clipboard.readText() pops the macOS "Paste" menu instead of returning the text. Desktop
      // only — mobile has no such chord and keeps the web Clipboard-API fallback (the command is macOS).
      if (!isMobile()) {
        setPlainClipboardReader(async () => {
          try {
            return await invoke<string>('read_clipboard_text');
          } catch {
            return null;
          }
        });
      }
    });
    // iOS augments the native selection callout with a "Paste Plain" item (ios-overlay/LapoPastePlain.swift);
    // it reads UIPasteboard natively and calls this bridge with the string, so the WebView never has to.
    if (isIOS()) {
      installNativePlainPasteBridge();
    }
    // Android has no keyboard chord and no way to add a "Paste Plain" item to wry's WebView action menu,
    // so its single native Paste is routed through the plain-text (no-markdown-parse) path instead.
    if (isAndroid()) {
      setPlainPasteDefault(true);
    }
  }, []);

  // Mobile keyboard: keep the shell — and the iconbar's touch-only indent/outdent controls — pinned
  // to the VISIBLE viewport while the soft keyboard is up. iOS ignores the interactive-widget
  // viewport directive and pans the pinned header off-screen without this; on Android the layout
  // viewport resizes instead, so the fix never engages (see shell/keyboardViewport.ts).
  useEffect(() => {
    if (!isMobile()) return;
    return installKeyboardViewportFix();
  }, []);

  // OAuth redirect handler for integration linking (Gmail/Calendar): web opens a popup and captures
  // the callback via postMessage; the Tauri shell opens the system browser and captures the provider
  // redirect through the app.lapo.client:// deep link.
  const redirect = useMemo(() => (isTauri() ? createTauriDeepLinkRedirect() : createWebPopupRedirect()), []);
  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: API_URL,
        getToken: async () => (sessionRef.current ? sessionRef.current.getIdToken() : null),
      }),
    [],
  );
  // SYSTEM-HEALTH — admin-only backend-health poll. Enabled only when signed in, API configured, and
  // the server flagged this user admin; non-admins never poll (zero overhead) and never see the dot
  // change. `forbidden` self-disables if the server ever 403s (admin flag out of sync).
  const health = useSystemHealth(session && API_URL ? api : null, isAdmin);
  const healthStatus = isAdmin && !health.forbidden ? health.status : undefined;
  // The curated event feed + raw-log drill-down are fetched on demand when the panel opens (the
  // HYBRID decision — the poll only carries the lightweight snapshot).
  const [healthEvents, setHealthEvents] = useState<HealthEventDTO[]>([]);
  const [healthEventsLoading, setHealthEventsLoading] = useState(false);
  const loadHealthEvents = useCallback(() => {
    if (!API_URL || !sessionRef.current) return;
    setHealthEventsLoading(true);
    api
      .getHealthEvents(200)
      .then((r) => setHealthEvents(r.events))
      .catch(() => setHealthEvents([]))
      .finally(() => setHealthEventsLoading(false));
  }, [api]);
  useEffect(() => {
    if (healthPanelOpen) loadHealthEvents();
  }, [healthPanelOpen, loadHealthEvents]);

  // UX-001: capture failures that escape React (uncaught errors, unhandled rejections) once, and route
  // crash reports through the authenticated api while signed in (cleared on sign-out — a signed-out POST
  // has no token to authenticate). Render errors are caught separately by the ErrorBoundary wrappers.
  useEffect(() => {
    installGlobalErrorHandlers();
  }, []);
  useEffect(() => {
    setClientErrorSink(session && API_URL ? (report) => api.postClientError(report) : null);
    return () => setClientErrorSink(null);
  }, [session, api]);
  // Web push for Daily Signals (SIG-08): once signed in on the web, register this browser so an open
  // tab is woken for new signals. Best-effort + guarded (native/unsupported/denied → focus fallback).
  useEffect(() => {
    if (!API_URL || !session || isTauri()) return;
    void initWebPush(api, readFirebaseConfig());
  }, [session, api]);
  // Toolbar-note (menu-bar scratchpad) config: synced across devices, editable in Settings →
  // Scratchpad. Only hand the hook a live api once we can authenticate, so signed-out stays local.
  const toolbarCfg = useToolbarConfig(session && API_URL ? api : null);
  // REALTIME — hold the signals SSE stream while signed in; wakes fan into DaySignals refetches.
  useSignalStream(API_URL ? api : null, Boolean(session));
  // Automations (engine pipeline) config + catalog: synced across devices, editable in Settings →
  // Automations. Local until we can authenticate, matching the toolbar/settings hooks.
  const engineCfg = useEngineConfig(session && API_URL ? api : null);
  // Watched-calendars setting (Automations): which Google calendars feed automations + agenda.
  const calendarPrefs = useCalendarPrefs(session && API_URL ? api : null);

  const onSignIn = useCallback(
    async (provider: LoginProvider) => {
      setAuthBusy(true);
      try {
        await signIn(provider);
      } catch (err) {
        pushToast({ tone: 'error', title: 'Sign-in failed', message: humanizeApiError(err) });
      } finally {
        setAuthBusy(false);
      }
    },
    [signIn, pushToast],
  );
  const onSignOut = useCallback(async () => {
    setAuthBusy(true);
    try {
      // Stop pushes to this device before we drop the session (best-effort; native only).
      await unregisterThisDevice(api);
      await signOut();
      await clearLocalData();
      // Reload so any still-mounted editors/persistence are torn down and we land cleanly on login.
      window.location.reload();
    } finally {
      setAuthBusy(false);
    }
  }, [signOut, api]);

  // Linked Google accounts (server-side, multiple supported). The server is the source of truth —
  // fetched from /me when the dialog is open and signed in. Only accounts that actually grant a data
  // integration (Gmail/Calendar) are shown; the identity-only sign-in account is excluded.
  // Linked accounts are server-side (source of truth), fetched from /me when the dialog opens. Google
  // accounts (Gmail/Calendar) show as a list; the identity-only sign-in account is excluded. Todoist
  // is a single linked account too — its token lives KMS-encrypted server-side, not in the client.
  const [accounts, setAccounts] = useState<GoogleAccountView[]>([]);
  const [todoist, setTodoist] = useState<{ accountId: string } | undefined>(undefined);
  const [easypost, setEasypost] = useState<{ accountId: string } | undefined>(undefined);
  const [claude, setClaude] = useState<{ accountId: string } | undefined>(undefined);
  const [granola, setGranola] = useState<{ accountId: string } | undefined>(undefined);
  const [tesseta, setTesseta] = useState<{ accountId: string } | undefined>(undefined);
  // True when a Google account is linked but NONE of them carry the Meet + Drive read scopes Gemini
  // Notes needs — so the Gemini card can prompt a proactive re-link before the first poll fails.
  const [geminiNeedsReconnect, setGeminiNeedsReconnect] = useState(false);
  const [authorizedApps, setAuthorizedApps] = useState<AuthorizedAppDTO[]>([]);
  // The display name as last known on the server (from /me). Drives one-time hydration into local
  // settings on sign-in and lets the commit-on-blur skip a no-op save. `undefined` = not loaded yet.
  const [serverProfileName, setServerProfileName] = useState<string | undefined>(undefined);
  // AXIS X24/X25 — server-stored biography + per-mailbox identity blocks (Settings → General / Google).
  const [serverBiography, setServerBiography] = useState<string>('');
  // Server-stored contact details (home/office addresses + phone numbers), hydrated from /me.
  const [contact, setContact] = useState<UserContact>({});
  const [accountIdentities, setAccountIdentities] = useState<Record<string, AccountIdentity>>({});
  // QUILL — the owner's writing-voice profile, working-hours window, and the quillVoice flag.
  const [writingStyle, setWritingStyle] = useState<WritingStyleProfile>(() => coerceWritingStyleProfile(undefined));
  const [workingHours, setWorkingHours] = useState<WorkingHours>(() => coerceWorkingHours(undefined));
  const [quillVoice, setQuillVoice] = useState(false);
  // AXIS X22/X27 — the master autonomy flag + global downstream-target defaults (Autonomy panel).
  const [axisEnabled, setAxisEnabled] = useState(false);
  const [actionDefaults, setActionDefaults] = useState<{ todoistProjectId?: string; taskLabel?: string; calendarId?: string }>({});
  // SIEVE S10 — per-source ingest-triage modes (Autonomy panel Filtered section).
  const [ingestTriageCfg, setIngestTriageCfg] = useState<IngestTriageConfig>(DEFAULT_INGEST_TRIAGE_CONFIG);
  // FOLD — the user's customized left-nav layout (order + separator + collapsed). Seeded to the
  // default until /me hydrates the server value; persisted server-side (debounced) so it syncs.
  const [menuLayout, setMenuLayout] = useState<MenuLayout>(() => defaultMenuLayout());
  // Latest-value ref so the reorder/collapse callbacks read the current layout without re-binding.
  const menuLayoutRef = useRef(menuLayout);
  menuLayoutRef.current = menuLayout;
  const menuLayoutSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string | undefined>(undefined);

  // Fan a /me response out into the account + preference state. Pure state writes (no network), so
  // it's reused to paint BOTH the cached snapshot (offline-first hydration, below) and the fresh
  // server response. Adopts the server display name locally, but the one-time upward migration of a
  // local-only name lives in refreshAccounts since it makes a network write.
  const applyMe = useCallback(
    (me: MeResponse) => {
      setAccounts(
        me.linkedAccounts
          .filter((a) => a.provider === 'google' && a.integrations.some((i) => i === 'gmail' || i === 'calendar' || i === 'people'))
          .map((a) => ({ accountId: a.accountId, email: a.email, integrations: a.integrations })),
      );
      const td = me.linkedAccounts.find((a) => a.integrations.includes('todoist'));
      setTodoist(td ? { accountId: td.accountId } : undefined);
      const ep = me.linkedAccounts.find((a) => a.integrations.includes('easypost'));
      setEasypost(ep ? { accountId: ep.accountId } : undefined);
      const cl = me.linkedAccounts.find((a) => a.integrations.includes('claude'));
      setClaude(cl ? { accountId: cl.accountId } : undefined);
      const gr = me.linkedAccounts.find((a) => a.integrations.includes('granola'));
      setGranola(gr ? { accountId: gr.accountId } : undefined);
      const ts = me.linkedAccounts.find((a) => a.integrations.includes('tesseta'));
      setTesseta(ts ? { accountId: ts.accountId } : undefined);
      // Gemini Notes rides Google but needs the escalated Meet + Drive scopes. If Google is linked but
      // no account has BOTH scopes, discovery can't reach Drive/Meet — surface a re-link prompt. (Gmail
      // discovery still works pre-relink, so this is advisory, not a hard block.)
      // Select real Google accounts by the calendar grant, NOT by provider — pasted-key accounts
      // (EasyPost/Granola/etc) also carry provider:'google' in the store.
      const googleAccts = me.linkedAccounts.filter((a) => a.integrations.includes('calendar'));
      const hasGeminiScopes = (a: (typeof googleAccts)[number]) =>
        a.scopes.includes('https://www.googleapis.com/auth/drive.readonly') &&
        a.scopes.includes('https://www.googleapis.com/auth/meetings.space.readonly');
      setGeminiNeedsReconnect(googleAccts.length > 0 && !googleAccts.some(hasGeminiScopes));
      // Display name is stored server-side (preferences.displayName) so it syncs across devices. Adopt
      // the server value into local settings when it differs (sign-in on a new device, or a change made
      // elsewhere).
      const serverName = typeof me.user.preferences?.displayName === 'string' ? me.user.preferences.displayName : '';
      setServerProfileName(serverName);
      // AXIS X24/X25 — hydrate biography + per-account identities from the same preferences map.
      setServerBiography(typeof me.user.preferences?.biography === 'string' ? me.user.preferences.biography : '');
      // normalizeUserContact migrates any legacy fixed-field data on read → the flexible labeled lists.
      setContact(normalizeUserContact(me.user.preferences?.contact));
      setAccountIdentities(
        me.user.preferences?.accountIdentities && typeof me.user.preferences.accountIdentities === 'object'
          ? (me.user.preferences.accountIdentities as Record<string, AccountIdentity>)
          : {},
      );
      // QUILL — hydrate the writing-voice profile, working hours, and the quillVoice flag.
      setWritingStyle(coerceWritingStyleProfile(me.user.preferences?.writingStyle));
      setWorkingHours(coerceWorkingHours(me.user.preferences?.workingHours));
      setQuillVoice(me.user.preferences?.quillVoice === true);
      // AXIS X22/X27 — the master autonomy flag (default OFF) + action-target defaults.
      setAxisEnabled(me.user.preferences?.axisAutonomy === true);
      setIngestTriageCfg(coerceIngestTriageConfig(me.user.preferences?.ingestTriage));
      // FOLD — hydrate the customized nav layout (coerce guards a corrupt/legacy blob).
      setMenuLayout(coerceMenuLayout(me.user.preferences?.menuLayout));
      setActionDefaults(
        me.user.preferences?.actionDefaults && typeof me.user.preferences.actionDefaults === 'object'
          ? (me.user.preferences.actionDefaults as { todoistProjectId?: string; taskLabel?: string; calendarId?: string })
          : {},
      );
      if (serverName && serverName !== settingsRef.current.displayName) updateSettings({ displayName: serverName });
      // SYSTEM-HEALTH — adopt the server's admin flag; gates the health indicator + detail panel.
      setIsAdmin(me.isAdmin === true);
    },
    [updateSettings],
  );

  const refreshAccounts = useCallback(async () => {
    if (!API_URL || !sessionRef.current) {
      setAccounts([]);
      setTodoist(undefined);
      setEasypost(undefined);
      setClaude(undefined);
      setGranola(undefined);
      setGeminiNeedsReconnect(false);
      setServerProfileName(undefined);
      setAuthorizedApps([]);
      return;
    }
    setAccountsLoading(true);
    setAccountsError(undefined);
    try {
      const me = await api.getMe();
      applyMe(me);
      // Cache the raw snapshot so the next launch (offline, or on a bad connection) can paint the full
      // account/profile state instantly, before this refresh even runs. Namespaced + cleared per the
      // same convention as the drawer panels (see panelCache / clearLocalData).
      const uid = sessionRef.current?.user.uid;
      if (uid) writePanelCache(panelCacheKey('me', uid), me);
      // If the server has no display name yet but this browser does, migrate the local name up once.
      const serverName = typeof me.user.preferences?.displayName === 'string' ? me.user.preferences.displayName : '';
      if (!serverName && settingsRef.current.displayName) {
        const local = settingsRef.current.displayName;
        void api
          .updateProfile({ displayName: local })
          .then(() => setServerProfileName(local))
          .catch(() => {
            /* best-effort migration; the next edit will retry the save */
          });
      }
      // Third-party OAuth apps the user has authorized (Lapo-as-provider). Best-effort — a failure here
      // shouldn't blank the linked-accounts list, so it's caught separately.
      setAuthorizedApps(await api.listAuthorizedApps().catch(() => []));
    } catch (err) {
      // Stale-while-revalidate: an offline/timeout failure (status 0) when we've already painted a
      // cached snapshot isn't a real error — keep the stale accounts and stay quiet. Only surface the
      // banner for genuine failures (or when there's nothing cached to fall back on).
      const uid = sessionRef.current?.user.uid;
      const offline = err instanceof ApiError && err.status === 0;
      const haveCache = !!uid && !!readPanelCache<MeResponse>(panelCacheKey('me', uid));
      if (!(offline && haveCache)) setAccountsError(`Couldn't load accounts: ${humanizeApiError(err)}`);
    } finally {
      setAccountsLoading(false);
    }
  }, [api, applyMe]);

  // Wake-and-sync: once signed in on the native shell, register this device's FCM token so the
  // server can push it to sync in the background. No-op on the web. Cleaned up on sign-out/unmount.
  useEffect(() => {
    if (!session) return;
    let unlisten = () => {};
    let cancelled = false;
    void initDeviceRegistration(api).then((fn) => {
      if (cancelled) fn();
      else unlisten = fn;
    });
    return () => {
      cancelled = true;
      unlisten();
    };
  }, [session, api]);

  // "Share to Lapo": once signed in on the native mobile shell, mint + store a share token so the OS
  // share-sheet handler can silently append shared links/text/images to today's journal. No-op on web/
  // desktop, and guarded to run once per user per install.
  useEffect(() => {
    if (!session) return;
    void initShareTarget(api, API_URL, session.user.uid, (err) =>
      console.warn('share-target provisioning failed', err),
    );
  }, [session, api]);

  // Offline-first: the moment we know the user, paint the last-known /me snapshot from local cache —
  // before (and regardless of whether) the network answers. refreshAccounts then reconciles in the
  // background and overwrites with fresh data when it lands. This is what makes the app "pop in" fully
  // populated on a cold launch in airplane mode or on a bad connection, instead of showing spinners.
  useEffect(() => {
    if (!session) return;
    const cached = readPanelCache<MeResponse>(panelCacheKey('me', session.user.uid));
    if (cached) applyMe(cached);
  }, [session, applyMe]);

  // Load accounts when the dialog opens (signed in).
  useEffect(() => {
    if (settingsOpen && session) void refreshAccounts();
  }, [settingsOpen, session, refreshAccounts]);

  // Also load once on sign-in so the Today-panel toggle can appear when Todoist is connected,
  // without waiting for the Settings dialog to be opened.
  useEffect(() => {
    if (session) void refreshAccounts();
  }, [session, refreshAccounts]);

  // Cold-start-safe link completion (native): if an account link was interrupted by the app being
  // killed during the browser OAuth (common on Android), the returning deep link cold-starts the app
  // here. Once signed in, read the launch URL and finish the exchange from the persisted pending
  // record — the live-promise path in onConnect* can't survive a process death. No-op on web/desktop
  // and when there's nothing pending. Runs once per boot.
  const resumedLinkRef = useRef(false);
  useEffect(() => {
    if (!session || !API_URL || !isTauri() || resumedLinkRef.current) return;
    resumedLinkRef.current = true;
    void (async () => {
      try {
        const resumed = await resumePendingLink(api);
        if (!resumed) return;
        await refreshAccounts();
        if (resumed.provider === 'google') calendarPrefs.reload();
        const label = resumed.provider === 'google' ? 'Google account' : 'Todoist';
        pushToast({ tone: 'success', message: `${label} connected.` });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not finish connecting', message: humanizeApiError(err) });
      }
    })();
  }, [session, api, refreshAccounts, calendarPrefs.reload, pushToast]);

  // Link another Google account via the real OAuth flow (begin → popup w/ account chooser →
  // complete). Each link adds a distinct account with its own Gmail + Calendar grant.
  const onConnectGoogle = useCallback(async () => {
    if (!API_URL) {
      pushToast({ tone: 'info', title: 'Integrations need an API', message: 'Set VITE_API_URL to connect accounts.' });
      return;
    }
    try {
      if (isMobile()) {
        // Android/iOS: the deep-link handoff of the OAuth code back into the app is unreliable, so use
        // the SERVER-BROKERED flow — the browser callback deposits the code, the server links the
        // account, and we poll for it. Robust even if Android kills the app mid-consent (the next
        // account refresh would surface it regardless).
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        await runBrokeredGoogleLink(api, { openUrl });
      } else {
        // Web popup / desktop deep link. Persist a cold-start-safe record on desktop so an interrupted
        // flow can still resume from the launch deep link.
        await runLink(api, redirect, 'google', [], (state) => {
          if (isTauri()) savePendingLink({ provider: 'google', state });
        });
        clearPendingLink();
      }
      await refreshAccounts();
      // A new Google grant brings new calendars — re-fetch the watched-calendars list so they show up
      // in the daily-agenda settings without a full app reload.
      calendarPrefs.reload();
      pushToast({ tone: 'success', message: 'Google account connected.' });
    } catch (err) {
      clearPendingLink();
      pushToast({ tone: 'error', title: 'Could not connect', message: humanizeApiError(err) });
    }
  }, [api, redirect, refreshAccounts, calendarPrefs.reload, pushToast]);

  // Link Todoist via OAuth (webhooks are only delivered to OAuth-authorized users): run the same
  // popup/redirect flow as Google, then refresh.
  const onConnectTodoist = useCallback(async () => {
    if (!API_URL) {
      pushToast({ tone: 'info', title: 'Integrations need an API', message: 'Set VITE_API_URL to connect accounts.' });
      return;
    }
    try {
      if (isMobile()) {
        // Android/iOS: the deep-link handoff of the OAuth code is unreliable, so use the SERVER-BROKERED
        // flow (mirrors onConnectGoogle/onConnectSlack) — the browser callback deposits the code, the
        // server links the account, and we poll for completion. Robust even if Android kills the app
        // mid-consent (the account stays "Not connected" otherwise, which is exactly this bug).
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        await runBrokeredTodoistLink(api, { openUrl });
      } else {
        await runTodoistLink(api, redirect, (state) => {
          if (isTauri()) savePendingLink({ provider: 'todoist', state });
        });
        clearPendingLink();
      }
      await refreshAccounts();
      pushToast({ tone: 'success', message: 'Todoist connected.' });
    } catch (err) {
      clearPendingLink();
      pushToast({ tone: 'error', title: 'Could not connect Todoist', message: humanizeApiError(err) });
    }
  }, [api, redirect, refreshAccounts, pushToast]);

  // Connect Tesseta (Fitness) via the browser OAuth (Authorization Code + PKCE) popup/redirect flow,
  // then refresh accounts + the config so the "Fitness" nav entry appears. The server kicks an initial
  // sync on completion, so the dashboard has data shortly after.
  const onConnectTesseta = useCallback(async () => {
    if (!API_URL) {
      pushToast({ tone: 'info', title: 'Integrations need an API', message: 'Set VITE_API_URL to connect accounts.' });
      return;
    }
    try {
      await runTessetaLink(api, redirect);
      clearPendingLink();
      await refreshAccounts();
      await api
        .getFitnessSummary()
        .then((r) => setTessetaConfig(r.config))
        .catch(() => {});
      pushToast({ tone: 'success', message: 'Tesseta connected — syncing your fitness data.' });
    } catch (err) {
      clearPendingLink();
      pushToast({ tone: 'error', title: 'Could not connect Tesseta', message: humanizeApiError(err) });
    }
  }, [api, redirect, refreshAccounts, pushToast]);

  // Toggle the sensitive assistant-reads opt-in for Tesseta (health data). Optimistic; reverts on error.
  const onToggleTessetaAssistantReads = useCallback(
    async (enabled: boolean) => {
      setTessetaConfig((c) => (c ? { ...c, assistantReads: enabled } : { enabled: true, assistantReads: enabled }));
      try {
        await api.setFitnessAssistantReads(enabled);
      } catch {
        setTessetaConfig((c) => (c ? { ...c, assistantReads: !enabled } : c));
      }
    },
    [api],
  );

  // Set the Tesseta weight display unit (lb/kg). Optimistic; reverts on error. Captures the prior unit
  // inside the state updater so it needn't read `tessetaConfig` (declared lower).
  const onSetTessetaWeightUnit = useCallback(
    async (unit: 'lb' | 'kg') => {
      let prev: 'lb' | 'kg' | undefined;
      setTessetaConfig((c) => {
        prev = c?.weightUnit;
        return c ? { ...c, weightUnit: unit } : { enabled: true, weightUnit: unit };
      });
      try {
        await api.setFitnessWeightUnit(unit);
      } catch {
        setTessetaConfig((c) => (c ? { ...c, weightUnit: prev } : c));
      }
    },
    [api],
  );

  // Send the pasted EasyPost API key to the server (stored KMS-encrypted), then refresh.
  const onConnectEasyPost = useCallback(
    async (apiKey: string) => {
      try {
        await api.linkEasyPost(apiKey);
        await refreshAccounts();
        pushToast({ tone: 'success', message: 'EasyPost connected.' });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not connect EasyPost', message: humanizeApiError(err) });
      }
    },
    [api, refreshAccounts, pushToast],
  );

  // Connect Claude from a pasted subscription token (BYO key). Stored KMS-encrypted server-side and
  // never returned to the client; powers the user's Claude workloads (e.g. the Subscription Digest).
  const onConnectClaude = useCallback(
    async (apiKey: string) => {
      try {
        await api.linkClaude(apiKey);
        await refreshAccounts();
        pushToast({ tone: 'success', message: 'Claude connected and verified.' });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not connect Claude', message: humanizeApiError(err) });
      }
    },
    [api, refreshAccounts, pushToast],
  );

  // Connect Granola from a pasted API key. Stored KMS-encrypted server-side; powers the meeting-notes
  // poller that nests each meeting's notes under its journal entry.
  const onConnectGranola = useCallback(
    async (apiKey: string) => {
      try {
        await api.linkGranola(apiKey);
        await refreshAccounts();
        pushToast({ tone: 'success', message: 'Granola connected.' });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not connect Granola', message: humanizeApiError(err) });
      }
    },
    [api, refreshAccounts, pushToast],
  );

  // Persist the display name to the server profile (so it syncs across devices) when the user commits
  // it — on blur. Local settings/localStorage already updated instantly for a snappy field; this is the
  // durable, cross-device store. No-op when signed out (localStorage is then the only store) or unchanged.
  const onCommitDisplayName = useCallback(
    (name: string) => {
      if (!API_URL || !sessionRef.current) return;
      if (name === (serverProfileName ?? '')) return;
      void api
        .updateProfile({ displayName: name })
        .then(() => setServerProfileName(name))
        .catch((err) => pushToast({ tone: 'error', title: "Couldn't save your name", message: humanizeApiError(err) }));
    },
    [api, serverProfileName, pushToast],
  );

  // AXIS X24 — persist the biography ("about me" LLM context) on blur, mirroring the name commit.
  const onCommitBiography = useCallback(
    (biography: string) => {
      if (!API_URL || !sessionRef.current) return;
      if (biography === serverBiography) return;
      void api
        .updateProfile({ biography })
        .then(() => setServerBiography(biography))
        .catch((err) => pushToast({ tone: 'error', title: "Couldn't save your bio", message: humanizeApiError(err) }));
    },
    [api, serverBiography, pushToast],
  );

  // Persist contact details (home/office addresses + phones) on blur. The modal sends the full merged
  // object; we update local state optimistically so the field reflects the save immediately.
  const onCommitContact = useCallback(
    (next: UserContact) => {
      if (!API_URL || !sessionRef.current) return;
      setContact(next);
      void api
        .updateProfile({ contact: next })
        .catch((err) => pushToast({ tone: 'error', title: "Couldn't save your contact info", message: humanizeApiError(err) }));
    },
    [api, pushToast],
  );

  // QUILL — persist the writing-voice profile / working hours / quillVoice flag. Optimistic local update.
  const onCommitWritingStyle = useCallback(
    (next: WritingStyleProfile) => {
      if (!API_URL || !sessionRef.current) return;
      setWritingStyle(next);
      void api
        .updateProfile({ writingStyle: next })
        .catch((err) => pushToast({ tone: 'error', title: "Couldn't save your writing profile", message: humanizeApiError(err) }));
    },
    [api, pushToast],
  );
  const onCommitWorkingHours = useCallback(
    (next: WorkingHours) => {
      if (!API_URL || !sessionRef.current) return;
      setWorkingHours(next);
      void api
        .updateProfile({ workingHours: next })
        .catch((err) => pushToast({ tone: 'error', title: "Couldn't save your working hours", message: humanizeApiError(err) }));
    },
    [api, pushToast],
  );
  const onChangeQuillVoice = useCallback(
    (on: boolean) => {
      if (!API_URL || !sessionRef.current) return;
      setQuillVoice(on);
      void api
        .updateProfile({ quillVoice: on })
        .catch((err) => {
          setQuillVoice(!on);
          pushToast({ tone: 'error', title: "Couldn't update the setting", message: humanizeApiError(err) });
        });
    },
    [api, pushToast],
  );
  const onBootstrapWritingStyle = useCallback(async () => {
    if (!API_URL || !sessionRef.current) return;
    try {
      const res = await api.bootstrapWritingStyle();
      setWritingStyle(coerceWritingStyleProfile(res.profile));
      pushToast({ tone: 'success', title: 'Drafted a voice profile', message: `From ${res.sampledMessages} sent messages. Review and approve it.` });
    } catch (err) {
      pushToast({ tone: 'error', title: "Couldn't analyze your sent mail", message: humanizeApiError(err) });
    }
  }, [api, pushToast]);

  // AXIS X22 — flip the master autonomy flag (the kill switch). Optimistic; reverts on failure.
  const onToggleAxis = useCallback(
    (enabled: boolean) => {
      if (!API_URL || !sessionRef.current) return;
      setAxisEnabled(enabled);
      void api.updateProfile({ axisAutonomy: enabled }).catch((err) => {
        setAxisEnabled(!enabled);
        pushToast({ tone: 'error', title: "Couldn't change autonomy", message: humanizeApiError(err) });
      });
    },
    [api, pushToast],
  );

  // AXIS X27 — persist the global downstream-target defaults from the Autonomy panel.
  const onCommitActionDefaults = useCallback(
    (defaults: { todoistProjectId?: string; taskLabel?: string; calendarId?: string }) => {
      if (!API_URL || !sessionRef.current) return;
      setActionDefaults(defaults);
      void api
        .updateProfile({ actionDefaults: defaults })
        .catch((err) => pushToast({ tone: 'error', title: "Couldn't save defaults", message: humanizeApiError(err) }));
    },
    [api, pushToast],
  );

  // SIEVE S10 — persist the per-source triage modes; optimistic with rollback on failure.
  const onCommitIngestTriage = useCallback(
    (cfg: IngestTriageConfig) => {
      if (!API_URL || !sessionRef.current) return;
      const prev = ingestTriageCfg;
      setIngestTriageCfg(cfg);
      void api.updateProfile({ ingestTriage: cfg }).catch((err) => {
        setIngestTriageCfg(prev);
        pushToast({ tone: 'error', title: "Couldn't save triage modes", message: humanizeApiError(err) });
      });
    },
    [api, ingestTriageCfg, pushToast],
  );

  // AXIS X25 — persist one mailbox's identity block (name/sign-off/role). Server merges per account id.
  const onCommitAccountIdentity = useCallback(
    (accountId: string, identity: AccountIdentity) => {
      if (!API_URL || !sessionRef.current) return;
      setAccountIdentities((prev) => ({ ...prev, [accountId]: identity }));
      void api
        .updateProfile({ accountIdentities: { [accountId]: identity } })
        .catch((err) =>
          pushToast({ tone: 'error', title: "Couldn't save the mailbox identity", message: humanizeApiError(err) }),
        );
    },
    [api, pushToast],
  );

  // Unlink any account (Google, Todoist, EasyPost). Disconnecting drops a data grant, so confirm
  // through the themed dialog first; on confirm the server also drops the account's stored secret.
  const onDisconnect = useCallback(
    (accountId: string) => {
      const isTodoist = todoist?.accountId === accountId;
      const isEasypost = easypost?.accountId === accountId;
      const isClaude = claude?.accountId === accountId;
      const isGranola = granola?.accountId === accountId;
      const label = isTodoist
        ? 'Todoist'
        : isEasypost
          ? 'EasyPost'
          : isClaude
            ? 'Claude'
            : isGranola
              ? 'Granola'
              : accounts.find((a) => a.accountId === accountId)?.email ?? 'this account';
      const grant = isTodoist
        ? 'your tasks'
        : isEasypost
          ? 'shipping labels'
          : isClaude
            ? 'Claude workloads'
            : isGranola
              ? 'your meeting notes'
              : `${label}'s Gmail and Calendar`;
      setConfirm({
        title: 'Disconnect account?',
        body: `Lapo will lose access to ${grant}. You can reconnect any time.`,
        onConfirm: () => {
          void (async () => {
            try {
              await api.unlink(accountId);
              await refreshAccounts();
              // Dropping a Google grant removes its calendars — refresh the watched list so they
              // disappear from the daily-agenda settings immediately.
              calendarPrefs.reload();
              pushToast({ tone: 'success', message: 'Account disconnected.' });
            } catch (err) {
              pushToast({ tone: 'error', title: 'Could not disconnect', message: humanizeApiError(err) });
            }
          })();
        },
      });
    },
    [api, accounts, todoist, easypost, claude, granola, refreshAccounts, calendarPrefs.reload, pushToast],
  );

  // Revoke a third-party OAuth authorization (Settings → Authorized apps). Confirm first, since the
  // external service immediately loses its access token.
  const onRevokeAuthorizedApp = useCallback(
    (grantId: string) => {
      const app = authorizedApps.find((a) => a.grantId === grantId);
      setConfirm({
        title: 'Revoke access?',
        body: `${app?.clientName ?? 'This app'} will immediately lose access to your account. It can be re-authorized later.`,
        onConfirm: () => {
          void (async () => {
            try {
              await api.revokeAuthorizedApp(grantId);
              setAuthorizedApps((prev) => prev.filter((a) => a.grantId !== grantId));
              pushToast({ tone: 'success', message: 'Access revoked.' });
            } catch (err) {
              pushToast({ tone: 'error', title: 'Could not revoke', message: humanizeApiError(err) });
            }
          })();
        },
      });
    },
    [api, authorizedApps, pushToast],
  );

  const integrations: IntegrationsState = {
    accounts,
    todoist,
    easypost,
    claude,
    granola,
    tesseta,
    authorizedApps: authorizedApps.map((a) => ({
      grantId: a.grantId,
      clientName: a.clientName,
      scopes: a.scopes,
      createdAt: a.createdAt,
      lastUsedAt: a.lastUsedAt,
    })),
    loading: accountsLoading,
    error: accountsError,
  };

  // Todoist "Today" — the content of the right-hand drawer (`rightOpen`). All of its state, the
  // load/refresh lifecycle (cache hydrate/persist, load-on-visible, in-flight coalescing), the
  // reconnect flag and the deferred (5s Undo window) task completion live in useTodoistToday.
  // `todoistPanelVisible` reproduces the inline effect's exact gate.
  const todoistPanelVisible = !!todoist && panelVisible('todoist');
  const {
    todoistToday,
    todoistLoading,
    todoistError,
    todoistReconnect,
    completingTasks,
    pendingUndo,
    loadTodoistToday,
    onTodoistRefresh,
    onTodoistConnect,
    onTodoistReconnect,
    onCompleteTask,
    onUndoComplete,
    onDismissUndo,
  } = useTodoistToday({
    api,
    session,
    pushToast,
    todoistPanelVisible,
    onConnectTodoist,
    onOpenSettings: () => setSettingsOpen(true),
  });

  // Agenda — the drawer's day view of every calendar event PLUS the server-side agenda settings edited on
  // the Integrations hub's Daily Agenda detail page. State, loaders, the load-on-visible + config-load
  // effects, day navigation, Save, and "Run now" all live in useAgenda. The panel keeps its OWN viewed
  // day (`agendaDateKey`), independent of the main journal date. `agendaPanelVisible` reproduces the
  // inline effect's exact gate; `agendaConfigVisible` gates the settings load; `calendarAccounts` feeds
  // the settings section object.
  const agendaPanelVisible = panelVisible('agenda');
  // The Weather integration's settings panel edits the same agenda-settings doc (location), so its
  // detail view must also trigger the settings load.
  const agendaConfigVisible =
    settingsOpen || (view.kind === 'integration' && (view.id === 'daily-agenda' || view.id === 'weather'));
  const {
    agendaDateKey,
    agendaDay,
    agendaDayLoading,
    agendaDayError,
    onAgendaRefresh,
    onAgendaPrevDay,
    onAgendaNextDay,
    onAgendaToday,
    agenda,
    onSaveAgenda,
    onRunAgenda,
  } = useAgenda({
    api,
    session,
    pushToast,
    agendaPanelVisible,
    agendaConfigVisible,
    calendarAccounts: accounts.filter((a) => a.integrations.includes('calendar')),
  });

  // EasyPost shipping module (ship-from address, purchased labels + drafts, package tracking) — all its
  // state, loaders, effects (cache hydrate/persist, load-on-visible, background backfill poll) and
  // actions live in useShipments. `shipmentsPanelVisible` reproduces the inline effect's exact gate.
  const shipmentsPanelVisible = panelVisible('shipping');
  const {
    shipFromAddress,
    shipFromLoading,
    shipFromSaving,
    shipFromError,
    onSaveShipFrom,
    shipments,
    shipDraft,
    setShipDraft,
    shipmentsLoading,
    shipBuying,
    shipSyncing,
    scanningEmail,
    shipRefreshingId,
    shipError,
    tracked,
    trackingBusy,
    refreshingTrackedId,
    loadTracked,
    onSyncShipments,
    onScanEmail,
    onBuyLabel,
    onRefreshShipment,
    onArchiveShipment,
    onEditShipment,
    onTrackPackage,
    onRefreshTracked,
    onUntrack,
    onArchiveTracked,
    onEditTracked,
  } = useShipments({
    api,
    session,
    easypost,
    pushToast,
    shipmentsPanelVisible,
    onLabelBought: closeRight,
  });

  // Google Contacts directory + reconciliation review queue (synced list, recency rail, suggestion
  // review, in-memory search + sync/merge/confirm/dismiss actions) — all its state, loaders and effects
  // live in useContacts. `needsContacts` reproduces the inline load-on-visible gate. `loadContacts` is
  // returned so the integration-run refresh and headshot-approve paths can still refresh the directory.
  const needsContacts =
    view.kind === 'contacts' || (view.kind === 'integration' && view.id === 'contact-pictures');
  const {
    contacts,
    recentContacts,
    contactSuggestions,
    contactQuery,
    setContactQuery,
    contactsLoading,
    contactsSyncing,
    contactsError,
    shownContacts,
    loadContacts,
    onSyncContacts,
    onMergeContacts,
    onConfirmSuggestion,
    onDismissSuggestion,
  } = useContacts({ api, session, pushToast, needsContacts });

  // Assistant palette (✦): route one natural-language line to an action. All its state, actions and
  // effects (send/thread/clear/capabilities, the bill-image + confirm-pay REMIT-CHAT flows, the DIAL
  // voice-call watcher, and the Esc-to-close handler) live in useAssistant. The cross-cluster tool
  // dispatch inside `onAssist` — add_task → refresh Today, create_shipping_label/track_package → drop a
  // draft into the Shipments drawer and jump there — CAN'T live in the hook (it touches Todoist,
  // Shipments and App's right-drawer layout), so App supplies it as named callbacks the hook invokes at
  // the exact dispatch point. Focus management (`useFocusTrap`) stays here since it needs App's ref.
  const assistantPopRef = useRef<HTMLDivElement>(null);
  const {
    assistantOpen,
    setAssistantOpen,
    assistantMessages,
    assistantBusy,
    assistantStatus,
    assistantError,
    assistantCapabilities,
    onAssist,
    onAssistBillImage,
    onConfirmBillPay,
    onAssistStop,
    onAssistClear,
  } = useAssistant({
    api,
    session,
    pushToast,
    onAddedTask: () => void loadTodoistToday(),
    onShipmentDraft: (draft) => {
      setShipDraft(draft);
      openRightPanel(SHIPPING_VIEW_INDEX); // surface the priced draft in the Shipments drawer
    },
    onTrackedPackage: () => {
      void loadTracked(); // pull the new tracked package into the Shipments list
      openRightPanel(SHIPPING_VIEW_INDEX);
    },
  });
  // Focus management for the ✦ assistant popover (audit F7): trap Tab, focus in on open, restore out.
  useFocusTrap(assistantPopRef, assistantOpen);

  const shipFrom: ShipFromSectionState | undefined =
    session && API_URL && shipFromAddress
      ? { address: shipFromAddress, loading: shipFromLoading, saving: shipFromSaving, error: shipFromError }
      : undefined;

  const account: AccountState = {
    configured,
    user: session ? { email: session.user.email, displayName: session.user.displayName } : null,
    busy: authBusy,
  };

  // Integrations hub: the top-level "Integrations" surface (a card of every workflow) drills into a
  // per-integration detail page. Live status/audit come from the server (GET /me/integrations);
  // `INTEGRATIONS` is the offline fallback so the grid renders before/without a network. Run triggers
  // the real workload (POST /me/integrations/:id/run) and refreshes that card from the response.
  // Declared here (above the config hooks) so `useFinancial` + every config hook can splice a refreshed
  // row after its run. The rest of the hub (enablement map, running set, run/toggle, the enablement +
  // live-list loaders) lives in useIntegrationsHub (called below, once all connection keys are in scope
  // — the list refetch keys on slackWorkspaces, whose config hook itself needs this setter).
  const [hubIntegrations, setHubIntegrations] = useState<Integration[]>(INTEGRATIONS);

  // Whether the Integrations-hub config blocks below can talk to the API at all. `api` is identity-
  // stable (created once), so this is the only load trigger each `useIntegrationConfig` needs.
  const integrationsEnabled = !!session && !!API_URL;

  // Subscription Digest config. Loaded on sign-in so the "Subscription" side-menu item can be gated on
  // the enabled flag; edited in the hub detail (updated on save).
  const subscriptionState = useIntegrationConfig<SubscriptionDigestConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getSubscriptionConfig(),
    save: async (next) => {
      const saved = await api.putSubscriptionConfig(next);
      pushToast({ tone: 'success', message: 'Subscription digest settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
    run: async () => {
      const updated = await api.runIntegration('subscription-digest');
      const note = updated.lastAction?.summary ?? 'Digest generated.';
      pushToast({ tone: 'success', message: note });
      setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      return note;
    },
    onRunError: (err) => pushToast({ tone: 'error', title: 'Could not run digest', message: err.message }),
  });
  const subscriptionConfig = subscriptionState.config;
  // Tesseta (Fitness): the config gates the "Fitness" nav entry (present + enabled once connected/synced).
  const [tessetaConfig, setTessetaConfig] = useState<{ enabled: boolean; assistantReads?: boolean; weightUnit?: 'lb' | 'kg' } | null>(null);
  useEffect(() => {
    if (!session || !API_URL) return;
    let cancelled = false;
    void api
      .getFitnessSummary()
      .then((r) => {
        if (!cancelled) setTessetaConfig(r.config);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session, API_URL, api]);
  const {
    financialConfig,
    financialItems,
    financialConnecting,
    reconnectingItemId,
    hostedPending,
    financialImporting,
    financial,
    onSaveFinancial,
    onRunFinancial,
    onConnectFinancial,
    onReconnectFinancial,
    onCancelHostedConnect,
    onImportMonarch,
    onDisconnectFinancialItem,
    loadFinancialCardholders,
    onSaveFinancialCardholder,
    refreshFinancialFromHub,
  } = useFinancial({ api, session, pushToast, integrationsEnabled, settingsOpen, setHubIntegrations });
  const subscription: SubscriptionSectionState | undefined =
    session && API_URL && subscriptionConfig
      ? {
          config: subscriptionConfig,
          saving: subscriptionState.saving,
          running: subscriptionState.running,
          error: subscriptionState.error,
          lastRunNote: subscriptionState.runNote,
        }
      : undefined;

  // Movie Enricher config (Integrations → Movie Enricher): the 2 AM nightly IMDb+streaming enrichment
  // of a Todoist project. Loaded on sign-in; edited in the hub detail.
  const movieEnricherState = useIntegrationConfig<MovieEnricherConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getMovieEnricherConfig(),
    save: async (next) => {
      const saved = await api.putMovieEnricherConfig(next);
      pushToast({ tone: 'success', message: 'Movie Enricher settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
    run: async () => {
      const updated = await api.runIntegration('movie-enricher');
      const note = updated.lastAction?.summary ?? 'Enriched movies.';
      pushToast({ tone: 'success', message: note });
      setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      return note;
    },
    onRunError: (err) => pushToast({ tone: 'error', title: 'Could not run Movie Enricher', message: err.message }),
  });
  const movieEnricherConfig = movieEnricherState.config;
  // Todoist projects (id + name) for the enricher project selectors. Loaded on sign-in; empty when
  // Todoist isn't linked (the section then falls back to a free-text project field). `needsReconnect`
  // is true when Todoist is linked but its token is expired/revoked (the projects fetch 401s) — the
  // selector then prompts a reconnect instead of silently showing a blank field.
  const todoistProjectsState = useIntegrationConfig<{ projects: TodoistProject[]; needsReconnect: boolean }>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: async () => {
      const r = await api.getTodoistProjects();
      return { projects: r.projects, needsReconnect: !!r.needsReconnect };
    },
  });
  const todoistProjects = todoistProjectsState.config?.projects ?? [];
  const todoistNeedsReconnect = todoistProjectsState.config?.needsReconnect ?? false;
  // Refetch the project list whenever the Todoist link (re)lands, so the enricher/verifier selectors
  // fill right away after a connect — the old settingsOpen-keyed refetch used to mask this.
  const todoistAccountId = todoist?.accountId;
  const reloadTodoistProjects = todoistProjectsState.reload;
  useEffect(() => {
    if (todoistAccountId) void reloadTodoistProjects();
  }, [todoistAccountId, reloadTodoistProjects]);
  const movieEnricher: MovieEnricherSectionState | undefined =
    session && API_URL && movieEnricherConfig
      ? {
          config: movieEnricherConfig,
          projects: todoistProjects,
          todoistNeedsReconnect,
          saving: movieEnricherState.saving,
          running: movieEnricherState.running,
          error: movieEnricherState.error,
          lastRunNote: movieEnricherState.runNote,
        }
      : undefined;

  // Gemini Notes config (Integrations → Gemini Notes): the ~15-min poller that imports Google Meet
  // "Take notes for me" summaries into the journal. No credential — it rides the linked Google account
  // (re-linked for the Meet/Drive scopes). Loaded on sign-in / when settings open; edited in the hub.
  const geminiState = useIntegrationConfig<GeminiConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getGeminiConfig(),
    save: async (next) => {
      const saved = await api.putGeminiConfig(next);
      pushToast({ tone: 'success', message: 'Gemini Notes settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
    run: async () => {
      const updated = await api.runIntegration('gemini');
      const note = updated.lastAction?.summary ?? 'Checked for meeting notes.';
      pushToast({ tone: 'success', message: note });
      setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      return note;
    },
    onRunError: (err) => pushToast({ tone: 'error', title: 'Could not run Gemini Notes', message: err.message }),
  });
  const gemini: GeminiSectionState | undefined =
    session && API_URL && geminiState.config
      ? {
          config: geminiState.config,
          googleLinked: accounts.some((a) => a.integrations.includes('calendar')),
          needsReconnect: geminiNeedsReconnect,
          saving: geminiState.saving,
          running: geminiState.running,
          error: geminiState.error,
          lastRunNote: geminiState.runNote,
        }
      : undefined;

  // Instagram Share config (Integrations → Instagram Share): the Scrape Creators API key behind the
  // phone-share → journal import. Loaded on sign-in; edited in the hub detail. The GET/PUT round-trip
  // only ever carries the sentinel for a stored key; Clear saves an empty key.
  const instagramShareState = useIntegrationConfig<InstagramShareConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getInstagramShareConfig(),
    save: async (next) => {
      const saved = await api.putInstagramShareConfig(next);
      pushToast({ tone: 'success', message: next.apiKey ? 'Instagram Share key saved.' : 'Instagram Share key cleared.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
  });
  // Add-by-URL: paste a post/reel link in the panel → same accept pipeline as the phone share.
  const [instagramImporting, setInstagramImporting] = useState(false);
  const onImportInstagramUrl = useCallback(
    async (url: string) => {
      setInstagramImporting(true);
      try {
        const result = await api.postInstagramImport(url, toDateKey(new Date()));
        if (result.status === 'accepted') {
          pushToast({ tone: 'success', message: "Importing — it'll appear in today's journal shortly." });
        } else if (result.status === 'duplicate') {
          pushToast({ tone: 'info', message: 'That post is already imported.' });
        } else {
          pushToast({ tone: 'error', title: 'Not configured', message: 'Save your Scrape Creators key first.' });
        }
      } catch (err) {
        pushToast({ tone: 'error', title: 'Import failed', message: humanizeApiError(err) });
      } finally {
        setInstagramImporting(false);
      }
    },
    [api, pushToast],
  );
  // In-editor Instagram import (`/instagram <url>` or a pasted-link affordance): the editor already
  // dropped the `gram-<importId>` placeholder at the caret's outline level, so we accept with
  // place='client' and the client-minted id. Returns whether it stuck (false reverts the placeholder).
  const onEditorImportInstagram = useCallback(
    async (url: string, importId: string, level: number): Promise<boolean> => {
      try {
        const result = await api.postInstagramImport(url, toDateKey(new Date()), { level, place: 'client', importId });
        if (result.status === 'accepted') return true;
        if (result.status === 'duplicate') pushToast({ tone: 'info', message: 'That post is already imported.' });
        else pushToast({ tone: 'error', title: 'Not configured', message: 'Save your Scrape Creators key first.' });
        return false;
      } catch (err) {
        pushToast({ tone: 'error', title: 'Import failed', message: humanizeApiError(err) });
        return false;
      }
    },
    [api, pushToast],
  );
  const onEditorRescanInstagram = useCallback(
    (importId: string) => {
      void api
        .rescanInstagramImport(importId)
        .then(() => pushToast({ tone: 'success', message: 'Re-scanning — the post will refresh shortly.' }))
        .catch((err) => pushToast({ tone: 'error', title: 'Re-scan failed', message: humanizeApiError(err) }));
    },
    [api, pushToast],
  );
  const onEditorResolveLink = useCallback((url: string) => api.resolveLinkMetadata(url).catch(() => null), [api]);
  const instagramShare: InstagramShareSectionState | undefined =
    session && API_URL && instagramShareState.config
      ? {
          config: instagramShareState.config,
          saving: instagramShareState.saving,
          importing: instagramImporting,
          error: instagramShareState.error,
        }
      : undefined;

  // Podcast Summaries (SCRIBE) — config + recent runs, the batch-summary run, and the in-editor
  // `/podcast` slash handler. Extracted to usePodcasts (deps + gates passed in).
  const { podcasts, onSavePodcasts, onSubmitPodcasts, onEditorSummarizePodcast } = usePodcasts({
    api,
    session,
    pushToast,
    integrationsEnabled,
    settingsOpen,
  });

  // Voice Calling settings (DIAL) — Vapi config + listed assistants/numbers + webhook flag. Extracted
  // to useVoice (deps + gates passed in). The assistant's place_call watcher stays in useAssistant.
  const { voiceCalling, onSaveVoiceCalling } = useVoice({
    api,
    session,
    pushToast,
    integrationsEnabled,
    settingsOpen,
  });

  // CREEL — Amazon (Clavero) settings: per-user BYO endpoint + two scope-split tokens + auto-confirm
  // dial + finance match window. Saving runs a live server-side `/v1/health` check (a failure surfaces
  // as `clavero-health-check-failed`). The masked config also gates the `shopping` nav id below.
  const claveroState = useIntegrationConfig<ClaveroConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getClaveroConfig(),
    save: async (next) => {
      const saved = await api.putClaveroConfig(next);
      pushToast({ tone: 'success', message: 'Amazon (Clavero) settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
  });
  // Bill Pay settings (REMIT): the card vault + BYO Bright Data endpoint + auto-pay cap. Card PAN/CVV
  // and the endpoint come back masked; a card added here rides its raw number/CVV to the server, which
  // encrypts + strips them. Deleting a card hits the dedicated DELETE route then refreshes.
  const billPayState = useIntegrationConfig<BillPayConfig, BillPayConfigSave>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getBillPayConfig(),
    save: async (next) => {
      const saved = await api.putBillPayConfig(next);
      pushToast({ tone: 'success', message: 'Bill Pay settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
  });
  const clavero: ClaveroSectionState | undefined =
    session && API_URL && claveroState.config
      ? { config: claveroState.config, saving: claveroState.saving, error: claveroState.error }
      : undefined;
  const billPay: BillPaySectionState | undefined =
    session && API_URL && billPayState.config
      ? { config: billPayState.config, addresses: contact.addresses ?? [], saving: billPayState.saving, error: billPayState.error }
      : undefined;
  const onDeleteBillPayCard = async (cardId: string) => {
    try {
      const saved = await api.deletePaymentMethod(cardId);
      billPayState.setConfig(saved);
      pushToast({ tone: 'success', message: 'Card removed.' });
    } catch (err) {
      pushToast({ tone: 'error', title: 'Could not remove card', message: humanizeApiError(err) });
    }
  };
  const onDeleteBillPayBank = async (bankId: string) => {
    try {
      const saved = await api.deleteBankAccount(bankId);
      billPayState.setConfig(saved);
      pushToast({ tone: 'success', message: 'Bank account removed.' });
    } catch (err) {
      pushToast({ tone: 'error', title: 'Could not remove bank account', message: humanizeApiError(err) });
    }
  };

  // Reddit Monitor settings: BYO redditapis key + watched subreddit channels + keyword filters. The
  // config is edited here (integration card); the filtered results render in the Reddit side-panel.
  const redditState = useIntegrationConfig<RedditConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getRedditConfig(),
    save: async (next) => {
      const saved = await api.putRedditConfig(next);
      pushToast({ tone: 'success', message: 'Reddit Monitor settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
  });
  const redditMonitor: RedditMonitorSectionState | undefined =
    session && API_URL && redditState.config
      ? { config: redditState.config, saving: redditState.saving, error: redditState.error }
      : undefined;

  // Decision Ledger settings (Enh-C): enable + confidence flag boundary + run/backfill. Backfill is a
  // second long-running action sharing the block's error/note surface, so it rides as a companion
  // callback writing through the hook's setError/setRunNote.
  const dlState = useIntegrationConfig<DecisionLedgerConfig, { enabled: boolean; confidenceThreshold: number }>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getDecisionLedgerConfig(),
    save: async (patch) => {
      const saved = await api.putDecisionLedgerConfig(patch);
      pushToast({ tone: 'success', message: 'Decision Ledger settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
    run: async () => {
      const r = await api.runDecisionLedger();
      const note = `${r.created} new · ${r.merged} merged · ${r.superseded} superseded`;
      pushToast({ tone: 'success', message: `Decision Ledger: ${note}` });
      return note;
    },
    onRunError: (err) => pushToast({ tone: 'error', title: 'Could not run Decision Ledger', message: err.message }),
  });
  const [dlBackfilling, setDlBackfilling] = useState(false);
  const { setError: setDlError, setRunNote: setDlRunNote } = dlState;
  const onBackfillDecisionLedger = useCallback(
    async (days: number) => {
      setDlBackfilling(true);
      setDlError(undefined);
      setDlRunNote(undefined);
      try {
        const r = await api.backfillDecisionLedger(days);
        const note = `Backfilled ${r.processed} days · ${r.created} decisions`;
        setDlRunNote(note);
        pushToast({ tone: 'success', message: note });
      } catch (err) {
        setDlError(humanizeApiError(err));
        pushToast({ tone: 'error', title: 'Backfill failed', message: humanizeApiError(err) });
      } finally {
        setDlBackfilling(false);
      }
    },
    [api, pushToast, setDlError, setDlRunNote],
  );
  const decisionLedger: DecisionLedgerSectionState | undefined =
    session && API_URL && dlState.config
      ? { config: dlState.config, saving: dlState.saving, running: dlState.running, backfilling: dlBackfilling, error: dlState.error, lastRunNote: dlState.runNote }
      : undefined;

  // ECHO — Note Resurfacing settings (Integrations → Note Resurfacing): the weekly opt-in + Run now.
  // The "config" here is just the boolean opt-in.
  const rsState = useIntegrationConfig<boolean>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: async () => (await api.getResurfaceConfig()).enabled,
    save: async (enabled) => {
      await api.setResurfaceConfig(enabled);
      pushToast({ tone: 'success', message: enabled ? 'Note Resurfacing on — first digest Monday.' : 'Note Resurfacing off.' });
      return enabled;
    },
    run: async () => {
      const r = await api.runResurface();
      const note = r.skipped ? 'Off — enable it first' : r.emitted ? `${r.items} resurfaced` : `Quiet week (${r.candidates} candidates, none worth it)`;
      pushToast({ tone: 'success', message: `Note Resurfacing: ${note}` });
      return note;
    },
    onRunError: (err) => pushToast({ tone: 'error', title: 'Could not run Note Resurfacing', message: err.message }),
  });
  const resurface: ResurfaceSectionState | undefined =
    session && API_URL && rsState.config != null
      ? { enabled: rsState.config, saving: rsState.saving, running: rsState.running, error: rsState.error, lastRunNote: rsState.runNote }
      : undefined;

  // Slack integration (Integrations → Slack): multi-workspace user-token OAuth + a nightly journal
  // digest. Loaded on sign-in; managed in the hub connect panel. Workspaces + config load together;
  // the connect/disconnect handlers below refresh via `reload`.
  const slackState = useIntegrationConfig<{ workspaces: SlackWorkspaceView[]; config: SlackConfig }>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: async () => {
      const [ws, cfg] = await Promise.all([api.listSlackWorkspaces(), api.getSlackConfig()]);
      return { workspaces: ws.workspaces, config: cfg };
    },
  });
  const slackStateConfig = slackState.config;
  const slackWorkspaces = useMemo(() => slackStateConfig?.workspaces ?? [], [slackStateConfig]);
  const slackConfig = slackStateConfig?.config;
  const refreshSlack = slackState.reload;
  const setSlackState = slackState.setConfig;
  const onConnectSlack = useCallback(async () => {
    if (!API_URL) {
      pushToast({ tone: 'info', title: 'Integrations need an API', message: 'Set VITE_API_URL to connect Slack.' });
      return;
    }
    try {
      if (isMobile()) {
        // Android/iOS: the deep-link handoff of the OAuth code is unreliable, so use the SERVER-BROKERED
        // flow (mirrors onConnectGoogle) — the browser callback deposits the code, the server links the
        // workspace, and we poll for completion. Robust even if Android kills the app mid-consent.
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        await runBrokeredSlackLink(api, { openUrl });
      } else {
        await runSlackLink(api, redirect);
      }
      await Promise.all([refreshAccounts(), refreshSlack()]);
      pushToast({ tone: 'success', message: 'Slack workspace connected.' });
    } catch (err) {
      pushToast({ tone: 'error', title: 'Could not connect Slack', message: humanizeApiError(err) });
    }
  }, [api, redirect, refreshAccounts, refreshSlack, pushToast]);
  const onDisconnectSlackWorkspace = useCallback(
    async (accountId: string, teamId: string) => {
      try {
        await api.unlinkSlackWorkspace(accountId, teamId);
        await Promise.all([refreshAccounts(), refreshSlack()]);
        pushToast({ tone: 'success', message: 'Slack workspace disconnected.' });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not disconnect', message: humanizeApiError(err) });
      }
    },
    [api, refreshAccounts, refreshSlack, pushToast],
  );
  const onToggleSlackWorkspace = useCallback(
    async (teamId: string, enabled: boolean) => {
      const ws = slackWorkspaces.find((w) => w.teamId === teamId);
      if (!ws) return;
      // Optimistic flip; a failed persist reloads the server truth.
      setSlackState((prev) =>
        prev ? { ...prev, workspaces: prev.workspaces.map((w) => (w.teamId === teamId ? { ...w, enabled } : w)) } : prev,
      );
      try {
        await api.putSlackWorkspace(teamId, { enabled, allow: ws.allow, deny: ws.deny });
      } catch (err) {
        void refreshSlack();
        pushToast({ tone: 'error', title: 'Could not update workspace', message: humanizeApiError(err) });
      }
    },
    [api, slackWorkspaces, setSlackState, refreshSlack, pushToast],
  );
  const onSaveSlackConfig = useCallback(
    async (next: SlackConfig) => {
      try {
        const saved = await api.putSlackConfig(next);
        setSlackState((prev) => ({ workspaces: prev?.workspaces ?? [], config: saved }));
        pushToast({ tone: 'success', message: 'Slack settings saved.' });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not save', message: humanizeApiError(err) });
      }
    },
    [api, setSlackState, pushToast],
  );
  const onRunSlack = useCallback(async () => {
    try {
      const r = await api.runSlackDigest();
      pushToast({
        tone: 'success',
        message: r.quiet ? 'Slack was quiet — posted a notice.' : `Posted ${r.items} item${r.items === 1 ? '' : 's'} to today's journal.`,
      });
    } catch (err) {
      pushToast({ tone: 'error', title: 'Could not run Slack digest', message: humanizeApiError(err) });
    }
  }, [api, pushToast]);

  // Restaurant Verifier config (Integrations → Restaurant Verifier): the 2 AM nightly Google Places
  // enrichment + closure flagging of a Todoist project. Loaded on sign-in; edited in the hub detail.
  const restaurantVerifierState = useIntegrationConfig<RestaurantVerifierConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => api.getRestaurantVerifierConfig(),
    save: async (next) => {
      const saved = await api.putRestaurantVerifierConfig(next);
      pushToast({ tone: 'success', message: 'Restaurant Verifier settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
    run: async () => {
      const updated = await api.runIntegration('restaurant-verifier');
      const note = updated.lastAction?.summary ?? 'Verified restaurants.';
      pushToast({ tone: 'success', message: note });
      setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      return note;
    },
    onRunError: (err) => pushToast({ tone: 'error', title: 'Could not run Restaurant Verifier', message: err.message }),
  });
  const restaurantVerifier: RestaurantVerifierSectionState | undefined =
    session && API_URL && restaurantVerifierState.config
      ? {
          config: restaurantVerifierState.config,
          projects: todoistProjects,
          todoistNeedsReconnect,
          saving: restaurantVerifierState.saving,
          running: restaurantVerifierState.running,
          error: restaurantVerifierState.error,
          lastRunNote: restaurantVerifierState.runNote,
        }
      : undefined;

  // Connect/manage handlers for the connectable integrations (Todoist, EasyPost, Amazon, Granola,
  // Financial Management, authorized apps). Passed to each detail page, which renders the matching panel
  // off `integration.connect`.
  const integrationConnect: IntegrationConnectProps = {
    integrationsState: integrations,
    shipFrom,
    onConnectTodoist,
    onConnectEasyPost,
    onConnectGranola,
    onConnectTesseta: () => void onConnectTesseta(),
    onOpenFitness: () => setView({ kind: 'fitness' }),
    tessetaAssistantReads: tessetaConfig?.assistantReads ?? false,
    onToggleTessetaAssistantReads: (enabled: boolean) => void onToggleTessetaAssistantReads(enabled),
    tessetaWeightUnit: tessetaConfig?.weightUnit ?? 'lb',
    onSetTessetaWeightUnit: (unit: 'lb' | 'kg') => void onSetTessetaWeightUnit(unit),
    slackWorkspaces,
    slackConfig,
    onConnectSlack: () => void onConnectSlack(),
    onDisconnectSlackWorkspace: (accountId, teamId) => void onDisconnectSlackWorkspace(accountId, teamId),
    onToggleSlackWorkspace: (teamId, enabled) => void onToggleSlackWorkspace(teamId, enabled),
    onSaveSlackConfig: (cfg) => void onSaveSlackConfig(cfg),
    onRunSlack: () => void onRunSlack(),
    onSaveShipFrom,
    onDisconnect,
    onRevokeAuthorizedApp,
    financialItems: financialItems.map((i) => ({ itemId: i.itemId, institutionName: i.institutionName, status: i.status, lastSyncAt: i.lastSyncAt })),
    financialMonarchSummary: financialConfig?.lastMonarchImport,
    onConnectFinancial: () => void onConnectFinancial(),
    onReconnectFinancialItem: (itemId) => void onReconnectFinancial(itemId),
    financialReconnectingItemId: reconnectingItemId,
    loadFinancialCardholders,
    onSaveFinancialCardholder,
    onDisconnectFinancialItem: (itemId) => void onDisconnectFinancialItem(itemId),
    onOpenFinances: () => setView({ kind: 'finances' }),
    onImportMonarch: (file) => void onImportMonarch(file),
    onCancelHostedConnect: () => onCancelHostedConnect(),
    financialConnecting,
    financialImporting,
    financialHostedPending: hostedPending,
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    disabledReason: session ? undefined : 'Sign in first',
  };

  // Integrations HUB: the enablement map (gates integration-owned nav items), the running-cards set, and
  // the run/toggle actions + enablement/live-list loaders. `hubIntegrations`/`setHubIntegrations` stay
  // App-owned (declared early so the config hooks above splice into them); passed in here. The live-list
  // refetch keys on the linked-account set (a connect/disconnect refires it — same dep array as before).
  // A `contact-pictures` run refreshes the contact directory (`loadContacts`, Contacts cluster) — App
  // supplies that as `onAfterRun`, invoked at the exact post-splice/post-toast point the inline code was.
  const {
    integrationEnablement,
    runningIntegrations,
    onRunIntegration,
    onToggleIntegration,
  } = useIntegrationsHub({
    api,
    session,
    pushToast,
    hubIntegrations,
    setHubIntegrations,
    connectionKeys: [todoist?.accountId, easypost?.accountId, granola?.accountId, authorizedApps.length, slackWorkspaces.length],
    onAfterRun: (id) => {
      // Contact Pictures writes photos onto contacts — refresh the directory so they show.
      if (id === 'contact-pictures') void loadContacts();
    },
  });

  // Editable settings for in-place-configured hub integrations (the Daily Agenda: its watched calendars
  // + full ruleset). Moved out of the old Settings → Daily Agenda section into the hub detail page.
  const integrationSettings: IntegrationSettingsProps = {
    agenda,
    onSaveAgenda,
    onRunAgenda,
    watchedCalendars: session && API_URL ? calendarPrefs.calendars : undefined,
    onToggleCalendar: session && API_URL ? calendarPrefs.setEnabled : undefined,
    subscription,
    onSaveSubscription: subscriptionState.save,
    onRunSubscription: subscriptionState.run,
    movieEnricher,
    onSaveMovieEnricher: movieEnricherState.save,
    onRunMovieEnricher: movieEnricherState.run,
    restaurantVerifier,
    onSaveRestaurantVerifier: restaurantVerifierState.save,
    onRunRestaurantVerifier: restaurantVerifierState.run,
    financial,
    onSaveFinancial,
    onRunFinancial,
    gemini,
    onSaveGemini: geminiState.save,
    onRunGemini: geminiState.run,
    instagramShare,
    onSaveInstagramShare: instagramShareState.save,
    onImportInstagramUrl,
    podcasts,
    onSavePodcasts,
    onSubmitPodcasts,
    voiceCalling,
    onSaveVoiceCalling,
    clavero,
    onSaveClavero: claveroState.save,
    billPay,
    onSaveBillPay: billPayState.save,
    onDeleteBillPayCard,
    onDeleteBillPayBank,
    redditMonitor,
    onSaveRedditMonitor: redditState.save,
    decisionLedger,
    onSaveDecisionLedger: dlState.save,
    onRunDecisionLedger: dlState.run,
    onBackfillDecisionLedger,
    resurface,
    onToggleResurface: rsState.save,
    onRunResurface: rsState.run,
  };

  // Note registry (architecture §4). On a configured device we enumerate the user's pages over
  // `GET /notes` so a fresh device sees everything it has, not just locally-touched notes. Offline /
  // unconfigured this stays empty and we run purely off the local index.
  const [registry, setRegistry] = useState<NoteRecord[]>([]);
  // Sync-on-resume backstop (wake-and-sync). Android may throttle the background FCM sync, so when the
  // app returns to the foreground we bump this tick to re-pull the note registry; the live editor
  // providers reconnect on their own. This guarantees "up to date when I open it" even if the
  // background pass didn't run.
  const [resumeTick, setResumeTick] = useState(0);
  useEffect(() => {
    const onResume = () => {
      if (document.visibilityState === 'visible') setResumeTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, []);
  // The current document's banner, reported up by DocEditor so it can render full-width (edge-to-edge
  // of the main pane) rather than being trapped in the centered text column.
  const [docBanner, setDocBanner] = useState<{ url: string; align: string | null; alignX: string | null; zoom: number | null; height: number | null } | null>(null);
  // Stable identity — DocEditor's banner effect depends on this, so an inline fn would loop forever.
  const onDocBanner = useCallback(
    (url: string | null, style: { align: string | null; alignX: string | null; zoom: number | null; height: number | null }) =>
      setDocBanner(url ? { url, ...style } : null),
    [],
  );
  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      const token = session ? await session.getIdToken() : null;
      const notes = await fetchNotes(token, ctrl.signal);
      setRegistry(notes);
      // Commit the registry's cross-device open stamps to the local index so Recent persists across a
      // background→refocus (mobile cold-starts the webview on resume, dropping `registry` state). Empty
      // result (offline / aborted) is a no-op, so this never wipes local Recent.
      noteIndex.reconcileRegistry(notes);
    })();
    return () => ctrl.abort();
  }, [session, resumeTick]);

  // Library categories (browse-by-topic). The server rolls the user's tags/topics up into category
  // blocks (computed on a schedule + lazily on read); we fetch the cached set. Empty offline, in which
  // case the Library falls back to a plain document list.
  const [categories, setCategories] = useState<Category[]>([]);
  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      const token = session ? await session.getIdToken() : null;
      setCategories(await fetchCategories(token, ctrl.signal));
    })();
    return () => ctrl.abort();
  }, [session]);

  // Local note index (localStorage, reactive) — the offline-first catalog that powers Favorites,
  // Recent, Library, and search. Merged with the server registry so a configured device sees both.
  const localNotes = useNoteIndex();
  const toggleStar = useToggleStar();
  const allNotes = useMemo(() => mergeNotes(localNotes, registry), [localNotes, registry]);

  const toItem = useCallback(
    (n: LocalNote): SidebarItem => ({
      id: n.noteId,
      label: displayTitle(n),
      // The page's own icon (emoji or a "material:" symbol) when set; else the generic per-type emoji.
      emoji: <NoteIcon icon={n.icon} type={n.type} size={16} />,
      starred: n.starred,
    }),
    [],
  );
  const favorites = useMemo(() => starredNotes(allNotes).map(toItem), [allNotes, toItem]);
  // Recent shows DOCUMENTS only (journals are reached via the date navigator), newest-first.
  const recents = useMemo(() => recentDocuments(allNotes).map(toItem), [allNotes, toItem]);


  // Memoized (audit F3): the Sidebar is wrapped in React.memo, so the nav array must keep its identity
  // across unrelated App re-renders. Deps: the current view (drives `active`), the config-enabled flags
  // that gate the integration-owned entries, and the journal anchor date (the accessory's props).
  const subscriptionNavEnabled = !!(subscriptionConfig?.enabled && isIntegrationEnabled(integrationEnablement, 'subscription-digest'));
  const financialNavEnabled = !!(financialConfig?.enabled && isIntegrationEnabled(integrationEnablement, 'financial-management'));
  const tessetaNavEnabled = !!(tessetaConfig?.enabled && isIntegrationEnabled(integrationEnablement, 'tesseta'));
  // CREEL — the `shopping` nav id shows once Amazon (Clavero) is configured (enabled) AND its master switch is on.
  const claveroNavEnabled = !!(claveroState.config?.enabled && isIntegrationEnabled(integrationEnablement, 'amazon'));
  // DOCKET D4 — the `bills` nav id shows once Bill Pay is enabled AND its master switch is on.
  const billsNavEnabled = !!(billPayState.config?.enabled && isIntegrationEnabled(integrationEnablement, 'bill-pay'));
  const nav: SidebarNavItem[] = useMemo(() => [
    {
      id: 'journals',
      label: 'Journals',
      active: view.kind === 'journals',
      icon: navIcon('book'),
      accessory: <JournalDateNav anchor={anchorDate} onPick={jumpToDate} />,
    },
    { id: 'pages', label: 'Library', active: view.kind === 'library', icon: navIcon('description') },
    { id: 'initiatives', label: 'Initiatives', active: view.kind === 'initiatives', icon: navIcon('flag') },
    { id: 'decisions', label: 'Decisions', active: view.kind === 'decisions', icon: navIcon('gavel') },
    { id: 'reddit', label: 'Reddit', active: view.kind === 'reddit', icon: navIcon('forum') },
    {
      id: 'ai-coaching',
      label: 'AI Coaching',
      active: view.kind === 'ai-coaching',
      icon: navIcon('psychology'),
      children: [
        {
          id: 'ai-coaching/overview',
          label: 'Overview',
          active: view.kind === 'ai-coaching' && view.module === 'overview',
          icon: navIcon('insights'),
        },
        {
          id: 'ai-coaching/review',
          label: 'Review queue',
          active: view.kind === 'ai-coaching' && view.module === 'review',
          icon: navIcon('checklist'),
        },
        {
          id: 'ai-coaching/guidance',
          label: 'Guidance',
          active: view.kind === 'ai-coaching' && view.module === 'guidance',
          icon: navIcon('menu_book'),
        },
        {
          id: 'ai-coaching/memory',
          label: 'Memory',
          active: view.kind === 'ai-coaching' && view.module === 'memory',
          icon: navIcon('psychology_alt'),
        },
        {
          id: 'ai-coaching/voice',
          label: 'Voice',
          active: view.kind === 'ai-coaching' && view.module === 'voice',
          icon: navIcon('record_voice_over'),
        },
        {
          id: 'ai-coaching/autonomy',
          label: 'Autonomy',
          active: view.kind === 'ai-coaching' && view.module === 'autonomy',
          icon: navIcon('auto_awesome'),
        },
        {
          id: 'ai-coaching/decisions',
          label: 'Decisions',
          active: view.kind === 'ai-coaching' && view.module === 'decisions',
          icon: navIcon('gavel'),
        },
        {
          id: 'ai-coaching/initiatives',
          label: 'Initiatives',
          active: view.kind === 'ai-coaching' && view.module === 'initiatives',
          icon: navIcon('flag'),
        },
        {
          id: 'ai-coaching/digests',
          label: 'Digests',
          active: view.kind === 'ai-coaching' && view.module === 'digests',
          icon: navIcon('newspaper'),
        },
        {
          id: 'ai-coaching/finances',
          label: 'Finances',
          active: view.kind === 'ai-coaching' && view.module === 'finances',
          icon: navIcon('account_balance'),
        },
        {
          id: 'ai-coaching/signals',
          label: 'Signals & noise',
          active: view.kind === 'ai-coaching' && view.module === 'signals',
          icon: navIcon('notifications'),
        },
      ],
    },
    { id: 'contacts', label: 'Contacts', active: view.kind === 'contacts' || view.kind === 'contact', icon: navIcon('contacts') },
    {
      id: 'integrations',
      label: 'Integrations',
      active: view.kind === 'integrations' || view.kind === 'integration',
      icon: navIcon('hub'),
    },
    {
      id: 'utilities',
      label: 'Utilities',
      active: view.kind === 'utilities' || view.kind === 'utility',
      icon: navIcon('build'),
    },
    // "Digests" reader shows when the digest is set up AND its master switch is on (hidden when suspended).
    ...(subscriptionNavEnabled
      ? [
          {
            id: 'subscription',
            label: 'Digests',
            active: view.kind === 'subscription',
            icon: navIcon('newspaper'),
          } as SidebarNavItem,
        ]
      : []),
    // "Finances" dashboard shows once Financial Management is connected AND its master switch is on.
    ...(financialNavEnabled
      ? [
          {
            id: 'finances',
            label: 'Finances',
            active: view.kind === 'finances',
            icon: navIcon('account_balance'),
          } as SidebarNavItem,
        ]
      : []),
    // "Fitness" dashboard shows once Tesseta is connected AND its master switch is on.
    ...(tessetaNavEnabled
      ? [
          {
            id: 'fitness',
            label: 'Fitness',
            active: view.kind === 'fitness',
            icon: navIcon('fitness_center'),
          } as SidebarNavItem,
        ]
      : []),
    // "Thank You" — employee thank-you letters (KUDOS). Owner-only; always available.
    {
      id: 'kudos',
      label: 'Thank You',
      active: view.kind === 'kudos',
      icon: navIcon('mail'),
    } as SidebarNavItem,
    // "Shopping" (Amazon via Clavero) shows once Amazon is configured AND its master switch is on.
    ...(claveroNavEnabled
      ? [
          {
            id: 'shopping',
            label: 'Shopping',
            active: view.kind === 'shopping',
            icon: navIcon('shopping_bag'),
          } as SidebarNavItem,
        ]
      : []),
    // "Bills" ledger (DOCKET D4) shows once Bill Pay is enabled AND its master switch is on.
    ...(billsNavEnabled
      ? [
          {
            id: 'bills',
            label: 'Bills',
            active: view.kind === 'bills',
            icon: navIcon('receipt_long'),
          } as SidebarNavItem,
        ]
      : []),
    // "Jobs" board (RELAY) — the agent's work ledger. Always available (no integration gate).
    {
      id: 'jobs',
      label: 'Jobs',
      active: view.kind === 'jobs',
      icon: navIcon('engineering'),
    } as SidebarNavItem,
  ], [view, anchorDate, jumpToDate, subscriptionNavEnabled, financialNavEnabled, tessetaNavEnabled, claveroNavEnabled, billsNavEnabled]);

  // Star/unstar any note by id, looking up its type/title from the merged list so a not-yet-indexed
  // note (e.g. an empty journal day) is created with the right shape. The local toggle is optimistic;
  // the server call persists `starred` so the favorite shows on every device (best-effort — offline
  // just keeps the optimistic local result, which reconciles on the next registry fetch).
  const onToggleStarById = useCallback(
    (noteId: string) => {
      const n = allNotes.find((x) => x.noteId === noteId);
      const starred = toggleStar(noteId, n?.type ?? noteTypeFromId(noteId), n?.title ?? '');
      setRegistry((rs) => rs.map((r) => (r.noteId === noteId ? { ...r, starred } : r)));
      if (sessionRef.current) void api.setStarred(noteId, starred).catch(() => {});
    },
    [allNotes, toggleStar, api],
  );

  // Dismiss a note from the Recent list (the X on a recents row): clear its open stamp. Optimistic in
  // both local sources (the index overlay + the registry snapshot) so it vanishes at once; the server
  // clears `openedAt` too, so it stays gone on every device until the note is opened again. Reopening
  // re-adds it. Best-effort server call (offline just keeps the optimistic local result).
  const onDismissRecent = useCallback(
    (noteId: string) => {
      noteIndex.clearOpened(noteId);
      setRegistry((rs) => rs.map((r) => (r.noteId === noteId ? { ...r, openedAt: null } : r)));
      if (sessionRef.current) void api.dismissRecent(noteId).catch(() => {});
    },
    [api],
  );

  // Unified `[[`/`#` autocomplete (IMPL-07). Local-first: the picker fills instantly from what this
  // device knows offline — the note catalog (every page title) plus a doc-name index kept current as
  // notes are edited — then the server entity index (`GET /entities/search`) is merged in when online.
  const localIndexRef = useRef<LocalNameIndex | null>(null);
  if (!localIndexRef.current) localIndexRef.current = new LocalNameIndex();

  // Live view of the merged catalog for the callbacks below (autocomplete + clickthrough), read from a
  // ref so those callbacks stay stable — recreating them would re-bind every open editor on each edit.
  const allNotesRef = useRef(allNotes);
  allNotesRef.current = allNotes;
  // Schedules a debounced server reindex of a note (assigned once pingReindex is defined below).
  const pingReindexRef = useRef<(noteId: string) => void>(() => {});

  // Every edit feeds two indexes: the autocomplete name index AND the note catalog (title + recency).
  // Empty future journal days are skipped (titleFromDoc is '' until written) so they don't list.
  const onDocNames = useCallback((noteId: string, doc: DocNodeJSON) => {
    // A remounted (windowed) day's first content report = its editor rehydrated; release the
    // placeholder min-height (no-op for every later report and for non-windowed notes).
    onDayHydrated(noteId);
    localIndexRef.current?.updateFromDoc(noteId, doc);
    const title = titleFromDoc(doc);
    const type = isDailyJournalId(noteId) ? PageType.Journal : PageType.Document;
    if (isDailyJournalId(noteId)) {
      if (title) noteIndex.touch(noteId, PageType.Journal, title, Date.now());
    } else {
      noteIndex.touch(noteId, PageType.Document, title || 'Untitled', Date.now());
    }
    // FIND-08: mirror the note's body into the on-device content index for instant local search.
    localContentIndex?.indexNote(noteId, type, title || 'Untitled', doc);
    pingReindexRef.current(noteId);
  }, [onDayHydrated]);

  // `local` is synchronous (catalog titles + open-doc names) so the popup never waits on the network;
  // `remote` hits the server index and is merged in when it answers. Offline → `remote` yields [].
  // Local cache of LOOM initiatives (id + name + color) so the `+` picker autocompletes offline and
  // can resolve a picked name → the stable initiative id for the `initiativeTag` atom (LOOM D-13).
  // Warmed from the LOOM board on sign-in and whenever the Initiatives view is opened.
  const loomInitiativesRef = useRef<Array<{ id: string; name: string; color: string }>>([]);
  // TRELLIS passive marker: blockId → initiative marker, warmed from the board. Read lazily by the
  // editor's decoration plugin so a block that belongs to an initiative gets a subtle inline icon.
  const loomMarkersRef = useRef<Map<string, InitiativeMarker>>(new Map());
  const initiativesOpen = view.kind === 'initiatives';
  useEffect(() => {
    if (!session || !API_URL) return;
    void api.loom
      .board()
      .then((board) => {
        loomInitiativesRef.current = board.initiatives.map((i) => ({ id: i.id, name: i.name, color: i.color }));
        const iniById = new Map(board.initiatives.map((i) => [i.id, i]));
        const markers = new Map<string, InitiativeMarker>();
        for (const p of board.placements) {
          if (!p.blockId || p.state !== 'placed') continue;
          const ini = iniById.get(p.initiativeId);
          if (!ini) continue;
          // Keep the first placement per block (a block rarely belongs to more than one initiative).
          if (markers.has(p.blockId)) continue;
          const status = effectiveStatus(ini);
          markers.set(p.blockId, {
            initiativeId: ini.id,
            name: ini.name,
            statusLine: `${statusLabel(status)} · ${lastActivityLabel(ini.lastActivityAt)}`,
          });
        }
        loomMarkersRef.current = markers;
      })
      .catch(() => undefined);
  }, [session, initiativesOpen, api]);

  const candidateSource = useMemo<CandidateSource>(
    () => ({
      local: (query, kind) => {
        // `+` autocompletes from the local LOOM initiative cache; `[[ ]]`/`#` stay initiative-free.
        if (kind === 'initiative') return rankCandidates(loomInitiativesRef.current.map((i) => i.name), stripQuarterSuffix(query));
        const catalog = allNotesRef.current.map((n) => displayTitle(n)).filter((t) => t && t !== 'Untitled');
        return rankCandidates([...catalog, ...(localIndexRef.current?.all() ?? [])], query);
      },
      remote: async (query, signal, kind) => {
        // Initiatives are no longer graph entities — the `+` picker is purely the local LOOM cache.
        if (kind === 'initiative') return [];
        const token = sessionRef.current ? await sessionRef.current.getIdToken() : null;
        return searchEntityIndex(query, token, 10, signal, 'exclude-initiative');
      },
      resolveInitiative: (name, query) => {
        const match = loomInitiativesRef.current.find((i) => i.name.toLowerCase() === name.toLowerCase());
        if (!match) return null;
        return { initiativeId: match.id, name: match.name, quarter: parseQuarterSuffix(query) };
      },
    }),
    [],
  );

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // The most recently focused editor, so the mobile toolbar's indent/outdent act on the bullet the
  // user is in (journals mount MANY day editors; a document mounts one). ProseMirror keeps its
  // selection even after the button steals DOM focus, so the command still targets the right line.
  const activeEditorRef = useRef<EditorHandle | null>(null);
  const setActiveEditor = useCallback((editor: EditorHandle) => {
    activeEditorRef.current = editor;
  }, []);
  const onIndentActive = useCallback(() => {
    const e = activeEditorRef.current;
    if (e && !e.isDestroyed) {
      indentListItem(e);
      e.commands.focus();
    }
  }, []);
  const onOutdentActive = useCallback(() => {
    const e = activeEditorRef.current;
    if (e && !e.isDestroyed) {
      outdentListItem(e);
      e.commands.focus();
    }
  }, []);

  // App-wide file drop (FileDropOverlay): a file dropped anywhere outside an editor / the assistant is
  // uploaded and attached to the note currently in focus — images embed inline, other files as a link
  // chip (the same uploadAndInsert engine the editor uses for in-text drops). With no note open, or with
  // uploads unconfigured, we point the user at what to do instead of silently dropping the file.
  const onDropFiles = useCallback(
    (files: File[]) => {
      if (!uploadFile) {
        pushToast({ tone: 'info', title: 'Uploads need an API', message: 'Set VITE_API_URL to attach files.' });
        return;
      }
      const e = activeEditorRef.current;
      if (!e || e.isDestroyed || !e.isEditable) {
        pushToast({ tone: 'info', title: 'Open a note to attach', message: 'Open a note or the journal, then drop files to attach them.' });
        return;
      }
      const container = (e.view.dom.parentElement as HTMLElement | null) ?? (e.view.dom as HTMLElement);
      uploadAndInsert(e, uploadFile, container, files);
      e.commands.focus();
    },
    [pushToast],
  );

  // Mobile multi-block selection: long-pressing a bullet turns on selection mode (via the editor's
  // BlockSelectionExtension, reported through onBlockSelection). While active, a floating action bar
  // drives the same group operations as the desktop keyboard shortcuts. The block operations DON'T
  // re-focus the editor — the selection lives in plugin state, not the DOM caret, and refocusing would
  // place a caret and clear it. `blockNumbered` mirrors the selection's marker so the toggle reads right.
  const [blockSelActive, setBlockSelActive] = useState(false);
  const [blockNumbered, setBlockNumbered] = useState(false);
  // Whether a move up/down is possible right now (drives the bar's greyed Up/Down buttons, D7). Moves
  // keep the selection active (no remount), so these are refreshed on selection-open AND after each move.
  const [canMoveUp, setCanMoveUp] = useState(false);
  const [canMoveDown, setCanMoveDown] = useState(false);
  const refreshMovable = useCallback((editor: EditorHandle | null, active: boolean): void => {
    setCanMoveUp(active && editor != null ? canMoveBlockSelection(editor, -1) : false);
    setCanMoveDown(active && editor != null ? canMoveBlockSelection(editor, 1) : false);
  }, []);
  const onBlockSelection = useCallback(
    (active: boolean, editor: EditorHandle) => {
      activeEditorRef.current = editor;
      setBlockSelActive(active);
      setBlockNumbered(active ? blockSelectionAllNumbered(editor) : false);
      refreshMovable(editor, active);
    },
    [refreshMovable],
  );
  const runBlockAction = useCallback((fn: (e: EditorHandle) => void): void => {
    const e = activeEditorRef.current;
    if (e && !e.isDestroyed) fn(e);
  }, []);
  const onBlockMoveUp = useCallback(
    () => runBlockAction((e) => { moveBlockSelection(e, -1); refreshMovable(e, true); }),
    [runBlockAction, refreshMovable],
  );
  const onBlockMoveDown = useCallback(
    () => runBlockAction((e) => { moveBlockSelection(e, 1); refreshMovable(e, true); }),
    [runBlockAction, refreshMovable],
  );
  const onBlockIndent = useCallback(
    () => runBlockAction((e) => { indentBlockSelection(e); refreshMovable(e, true); }),
    [runBlockAction, refreshMovable],
  );
  const onBlockOutdent = useCallback(
    () => runBlockAction((e) => { outdentBlockSelection(e); refreshMovable(e, true); }),
    [runBlockAction, refreshMovable],
  );
  const onBlockNumber = useCallback(
    () =>
      runBlockAction((e) => {
        toggleNumbered(e);
        setBlockNumbered(blockSelectionAllNumbered(e));
      }),
    [runBlockAction],
  );
  // Copy the selected blocks to the clipboard as markdown, keeping the selection (the bar stays up).
  // Async, so surface a toast on success/failure rather than blocking the tap.
  const onBlockCopy = useCallback(
    () =>
      runBlockAction((e) => {
        void copyBlockSelection(e).then((ok) =>
          pushToast(
            ok
              ? { tone: 'success', message: 'Copied as markdown' }
              : { tone: 'error', message: 'Could not copy to clipboard' },
          ),
        );
      }),
    [runBlockAction, pushToast],
  );
  // Delete + Done both clear the plugin selection, which fires onBlockSelection(false) → the bar unmounts.
  const onBlockDelete = useCallback(() => runBlockAction(deleteBlockSelection), [runBlockAction]);
  const onBlockDone = useCallback(() => runBlockAction(clearBlockSelection), [runBlockAction]);

  // Open a note from anywhere (sidebar, library, search). Journals re-anchor the day stack; documents
  // open in the single-doc view.
  const openNote = useCallback(
    (noteId: string, scrollToBlockId?: string) => {
      setSidebarOpen(false);
      setSearchOpen(false);
      const d = dateFromJournalId(noteId);
      if (d) {
        jumpToDate(d);
        setView({ kind: 'journals' });
      } else {
        // Record the open for the cross-device Recent list — optimistic locally, persisted server-side.
        // Journals are excluded from Recent, so only documents are stamped.
        const n = allNotesRef.current.find((x) => x.noteId === noteId);
        noteIndex.markOpened(noteId, n?.type ?? PageType.Document, n?.title ?? '', Date.now());
        if (sessionRef.current) void api.openNote(noteId).catch(() => {});
        setView({ kind: 'doc', noteId, scrollToBlockId });
      }
    },
    [jumpToDate, api],
  );

  // LENS-05 "peek": Shift+Enter from search opens a note WITHOUT losing the palette — a new browser tab
  // on web (the closest honest analog to Logseq's open-in-sidebar; Lapo has no note-peek drawer). When
  // window.open is unavailable/blocked (Tauri, mobile, popup-blocked) it falls back to a normal open.
  const peekNote = useCallback(
    (noteId: string, blockId?: string) => {
      const d = dateFromJournalId(noteId);
      const hash = d ? `#/journals` : `#/doc/${encodeURIComponent(noteId)}`;
      if (typeof window !== 'undefined' && typeof window.open === 'function') {
        const url = `${window.location.pathname}${window.location.search}${hash}`;
        const win = window.open(url, '_blank');
        if (win) return; // opened in a new tab; palette stays put in this one
      }
      openNote(noteId, blockId);
    },
    [openNote],
  );

  // Create (sidebar + library + search): mint a blank document, list it immediately, and open it.
  // An optional title seeds it (used by search's "create a document called …").
  const createDocument = useCallback((title?: string) => {
    const noteId = newDocumentId();
    const clean = title?.trim();
    noteIndex.touch(noteId, PageType.Document, clean || 'Untitled', Date.now());
    // Creating a doc opens it — stamp it into Recent (optimistic + server) like any other open.
    noteIndex.markOpened(noteId, PageType.Document, clean || 'Untitled', Date.now());
    if (sessionRef.current) void api.openNote(noteId).catch(() => {});
    setSidebarOpen(false);
    setSearchOpen(false);
    setView({ kind: 'doc', noteId, initialTitle: clean || undefined });
  }, [api]);
  // Zero-arg wrapper for the memoized Sidebar's "+" button (audit F3) — an inline arrow would defeat
  // the Sidebar's React.memo on every App re-render.
  const onSidebarCreate = useCallback(() => {
    createDocument();
  }, [createDocument]);

  // Open (or create + bind) the meeting note for a calendar event. Binding by eventId lets the
  // Initiatives calendar bridge propagate the note's +initiative mentions onto the event (R2).
  const openEventNote = useCallback(
    async (eventId: string, title: string) => {
      const existing = sessionRef.current ? await api.noteForEvent(eventId).catch(() => null) : null;
      if (existing) {
        openNote(existing);
        return;
      }
      const noteId = newDocumentId();
      const clean = title.trim() || 'Meeting notes';
      noteIndex.touch(noteId, PageType.Document, clean, Date.now());
      noteIndex.markOpened(noteId, PageType.Document, clean, Date.now());
      if (sessionRef.current) {
        void api.openNote(noteId).catch(() => {});
        void api.bindNoteToEvent(noteId, eventId).catch(() => {});
      }
      setSidebarOpen(false);
      setView({ kind: 'doc', noteId, initialTitle: clean });
    },
    [api, openNote],
  );
  // Stable, void-returning wrapper for the memoized AgendaPanel (audit F3).
  const onOpenAgendaEventNote = useCallback(
    (eventId: string, title: string) => void openEventNote(eventId, title),
    [openEventNote],
  );

  // Offline clickthrough resolver: maps a `[[name]]`/`#tag`/alias to its page id with no round-trip,
  // fed from the merged catalog (titles) and each open doc's aliases (via onAliases). Consumed by
  // openPageByName below — it's the instant local step before any server fallback.
  const resolverRef = useRef<LinkResolver | null>(null);
  if (!resolverRef.current) resolverRef.current = new LinkResolver();
  useEffect(() => {
    resolverRef.current?.setNotes(allNotes);
  }, [allNotes]);
  const onAliases = useCallback((noteId: string, aliases: string[]) => {
    resolverRef.current?.setAliases(noteId, aliases);
  }, []);

  // Discard a blank document when the user leaves it (DocEditor decides "blank" on unmount). Remove it
  // from the local catalog and Yjs cache, and — when signed in — delete it server-side too so it can't
  // reappear on the next load. Journals are never discarded (DocEditor is documents only).
  const onDiscardEmpty = useCallback(
    (noteId: string) => {
      noteIndex.remove(noteId);
      // Also drop it from the server-registry snapshot — allNotes = merge(local, registry), so without
      // this the page reappears in Library/Recents until the next registry refetch.
      setRegistry((prev) => prev.filter((n) => n.noteId !== noteId));
      void deleteNoteCache(noteId);
      if (session) void api.deleteNote(noteId).catch(() => {});
    },
    [session, api],
  );

  // Explicit "Delete page" from the ⋯ menu. Destructive and irreversible, so route it through the
  // themed confirm dialog; on confirm, drop it locally + server-side (like discard) and leave the page.
  const deleteDocument = useCallback(
    (noteId: string) => {
      const title = allNotes.find((n) => n.noteId === noteId)?.title || 'Untitled';
      setConfirm({
        title: 'Delete page?',
        body: `“${title}” will be permanently deleted. This can’t be undone.`,
        confirmLabel: 'Delete',
        onConfirm: async () => {
          // Snapshot for rollback if the server delete fails (UX-003: don't claim success on failure).
          const prevRegistry = registry;
          // Optimistic local removal. Drop from the server-registry snapshot too, or the merge
          // (allNotes) re-adds it to Library/Recents until the next refetch.
          noteIndex.remove(noteId);
          setRegistry((prev) => prev.filter((n) => n.noteId !== noteId));
          void deleteNoteCache(noteId);
          setView({ kind: 'library' });
          if (!session) {
            // Offline / signed-out: the note only ever lived on this device. Distinct copy so the
            // user knows nothing was removed server-side.
            pushToast({ tone: 'success', message: 'Page deleted from this device.' });
            return;
          }
          try {
            await api.deleteNote(noteId);
            pushToast({ tone: 'success', message: 'Page deleted.' });
          } catch (err) {
            // Server rejected the delete — restore the optimistic removal so the page doesn't silently
            // reappear on the next refetch after we falsely reported success.
            setRegistry(prevRegistry);
            noteIndex.reconcileRegistry(prevRegistry);
            pushToast({ tone: 'error', title: 'Delete failed', message: humanizeApiError(err) });
          }
        },
      });
    },
    [allNotes, registry, session, api, pushToast],
  );

  const onNav = useCallback(
    (id: string) => {
      setSidebarOpen(false);
      // Journals always lands on the live "today" (and resumes rollover tracking), so the morning's
      // entry is there without a manual date pick — even if the anchor drifted while paged back.
      if (id === 'journals') {
        jumpToDate(startOfToday());
        setView({ kind: 'journals' });
      } else if (id === 'pages') setView({ kind: 'library' });
      else if (id === 'initiatives') setView({ kind: 'initiatives' });
      else if (id === 'decisions') setView({ kind: 'decisions' });
      else if (id === 'reddit') setView({ kind: 'reddit' });
      else if (id === 'contacts') setView({ kind: 'contacts' });
      else if (id === 'integrations') setView({ kind: 'integrations' });
      else if (id === 'utilities') setView({ kind: 'utilities' });
      else if (id === 'subscription') setView({ kind: 'subscription' });
      else if (id === 'finances') setView({ kind: 'finances' });
      else if (id === 'fitness') setView({ kind: 'fitness' });
      else if (id === 'kudos') setView({ kind: 'kudos' });
      else if (id === 'shopping') setView({ kind: 'shopping' });
      else if (id === 'bills') setView({ kind: 'bills' });
      else if (id === 'jobs') setView({ kind: 'jobs' });
      else if (id === 'ai-coaching') setView({ kind: 'ai-coaching', module: 'overview' });
      else if (id.startsWith('ai-coaching/'))
        setView({ kind: 'ai-coaching', module: coachingModuleFrom(id.slice('ai-coaching/'.length)) });
    },
    [jumpToDate],
  );

  // FOLD — persist the customized nav layout server-side, debounced so a flurry of drags (or rapid
  // collapse toggles) coalesces into one write. Local state updates optimistically so the UI never
  // lags the gesture; the save is best-effort (offline keeps the optimistic result, reconciles on the
  // next /me). A pending timer is flushed on unmount so the last change isn't lost.
  const saveMenuLayout = useCallback(
    (next: MenuLayout) => {
      setMenuLayout(next);
      if (menuLayoutSaveRef.current) clearTimeout(menuLayoutSaveRef.current);
      menuLayoutSaveRef.current = setTimeout(() => {
        menuLayoutSaveRef.current = null;
        if (sessionRef.current) void api.updateProfile({ menuLayout: next }).catch(() => {});
      }, 500);
    },
    [api],
  );
  const onMenuReorder = useCallback(
    (order: string[]) => saveMenuLayout(reorderMenuLayout(menuLayoutRef.current, order)),
    [saveMenuLayout],
  );
  const onMenuToggleCollapse = useCallback(
    () => saveMenuLayout({ ...menuLayoutRef.current, collapsed: !menuLayoutRef.current.collapsed }),
    [saveMenuLayout],
  );
  // FOLD (spec D14) — reset the nav to its default order/separator/collapsed and persist immediately.
  const onMenuReset = useCallback(() => {
    const next = defaultMenuLayout();
    setMenuLayout(next);
    if (menuLayoutSaveRef.current) clearTimeout(menuLayoutSaveRef.current);
    menuLayoutSaveRef.current = null;
    if (sessionRef.current) void api.updateProfile({ menuLayout: next }).catch(() => {});
  }, [api]);
  // Flush any pending debounced layout save when the app unmounts.
  useEffect(
    () => () => {
      if (menuLayoutSaveRef.current) {
        clearTimeout(menuLayoutSaveRef.current);
        menuLayoutSaveRef.current = null;
        if (sessionRef.current) void api.updateProfile({ menuLayout: menuLayoutRef.current }).catch(() => {});
      }
    },
    [api],
  );

  // (`hubIntegrations` + the run/toggle actions, enablement map, and running-cards set live in
  // useIntegrationsHub — see the hook call up by `integrationConnect`.) `onOpenIntegration` stays here
  // since it drives App's nav/scroll-restore (integrationsScrollRef / mainRef / setSidebarOpen / setView).
  const onOpenIntegration = useCallback((id: string) => {
    // Remember where the Integrations list was scrolled so we can restore it when the user comes back.
    integrationsScrollRef.current = mainRef.current?.scrollTop ?? 0;
    setSidebarOpen(false);
    setView({ kind: 'integration', id });
  }, []);

  // Utilities hub: the top-level "Utilities" surface (maintenance workloads run on demand). Gmail Cleanup
  // is the first. Live status comes from GET /me/utilities; `UTILITIES` is the offline fallback. The
  // detail page (UtilityDetail) fetches its own config/run + polls live progress, so here we only own the
  // grid + navigation, and the "Grant delete permission" OAuth hop.
  const [utilities, setUtilities] = useState<Utility[]>(UTILITIES);
  // Gate the refetch on the Utilities surface actually being visible — `view.kind` in the deps used to
  // refire GET /me/utilities on EVERY navigation anywhere in the app (audit F5).
  const utilitiesVisible = view.kind === 'utilities' || view.kind === 'utility';
  useEffect(() => {
    if (!session || !API_URL) return;
    let cancelled = false;
    void api
      .listUtilities()
      .then((list) => {
        if (!cancelled && list.length) setUtilities(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session, api, granola?.accountId, utilitiesVisible]);
  const onOpenUtility = useCallback((id: string) => {
    setSidebarOpen(false);
    setView({ kind: 'utility', id });
  }, []);
  // The separate "Grant delete permission" re-consent (adds gmail.modify server-side, on top of the base
  // scopes). Uses the web/desktop redirect flow (the surface Gmail Cleanup is driven from); the server
  // route reuses the existing account by (provider, externalId). On success the detail reloads and the
  // account row flips to "granted". (Mobile brokered grant is a follow-up — see decision log.)
  const onGrantGmailModify = useCallback(async () => {
    if (!API_URL) {
      pushToast({ tone: 'info', title: 'Utilities need an API', message: 'Set VITE_API_URL to grant permissions.' });
      return;
    }
    await runGmailModifyGrant(api, redirect, (state) => {
      if (isTauri()) savePendingLink({ provider: 'google', state });
    });
    clearPendingLink();
    await refreshAccounts();
    pushToast({ tone: 'success', message: 'Delete permission granted.' });
  }, [api, redirect, refreshAccounts, pushToast]);

  // Contact Pictures headshot studio: project the synced directory into the picker's minimal shape, and
  // on approval splice the refreshed integration (new audit line) into the hub + refresh the directory
  // so the new photo shows.
  const headshotContacts = useMemo<HeadshotStudioContact[]>(
    () =>
      contacts.map((c) => ({
        id: c.contactId,
        name: c.displayName ?? c.emails[0] ?? 'Unknown',
        subtitle: c.org ?? c.title ?? c.emails[0],
        photoUrl: c.photoUrl,
      })),
    [contacts],
  );
  const onApproveHeadshot = useCallback(
    async (draftId: string) => {
      const res = await api.approveHeadshot(draftId);
      const updated = res.integration;
      if (updated) setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      void loadContacts();
      pushToast({ tone: 'success', message: 'Headshot saved to the contact.' });
    },
    [api, loadContacts, pushToast],
  );

  // The list view carries status + last action but not the full audit log; when a detail page opens,
  // fetch the complete record (GET /me/integrations/:id) and merge it into the list.
  useEffect(() => {
    if (view.kind !== 'integration' || !session || !API_URL) return;
    const id = view.id;
    let cancelled = false;
    void api
      .getIntegration(id)
      .then((full) => {
        if (!cancelled && full) setHubIntegrations((prev) => prev.map((i) => (i.id === id ? full : i)));
      })
      .catch(() => {});
    // Financial Management: also re-pull the config + items so a change made on another device (e.g. a
    // Monarch import on web) shows here without an app restart — the summary/rules live on the config.
    if (id === 'financial-management') {
      void refreshFinancialFromHub();
    }
    return () => {
      cancelled = true;
    };
  }, [view, session, api, refreshFinancialFromHub]);

  // Click a [[wikilink]]/#tag → open its page (IMPL-07, Logseq "click a link to open its page"). The
  // page is a real note titled by the name: resolve it locally first (offline resolver — matches titles
  // AND aliases, punctuation-insensitively; instant, no round-trip), else ask the server, else create
  // the page (clicking a non-existent link creates it).
  const openPageByName = useCallback(
    (name: string) => {
      if (!name.trim()) return;
      const localId = resolverRef.current?.resolve(name) ?? null;
      if (localId) {
        openNote(localId);
        return;
      }
      void (async () => {
        try {
          const r = await api.resolvePage(name);
          if (r.exists && r.noteId) {
            openNote(r.noteId);
            return;
          }
        } catch {
          // offline / unconfigured → fall through to local creation
        }
        createDocument(name);
      })();
    },
    [api, openNote, createDocument],
  );
  const onNavigate = useCallback(
    (target: { kind: 'wikilink' | 'tag'; name: string }) => {
      openPageByName(target.name);
    },
    [openPageByName],
  );

  // A LOOM `+` badge click deletes it (the editor already removed the atom). Fire an immediate
  // DELETE so the veto lands at once (D-20/D-11); the store-hook reconcile on the next sync is the
  // backstop. The undo toast re-inserts the atom → a manual re-tag that re-adds the placement and
  // clears the veto. itemKey mirrors reconcileNoteTags: whole-doc → note:…, else block:…#….
  const onInitiativeTagDelete = useCallback(
    (
      info: { initiativeId: string; name: string; quarter: string | null; blockId: string | null; wholeDoc: boolean; noteId: string },
      undo: () => void,
    ) => {
      const itemKey = info.wholeDoc || !info.blockId ? `note:${info.noteId}` : `block:${info.noteId}#${info.blockId}`;
      void api.loom.removePlacement(itemKey).catch(() => undefined);
      pushToast({ message: `Removed +${info.name}`, tone: 'info', action: { label: 'Undo', onClick: undo } });
    },
    [api, pushToast],
  );

  // Resolve a LOOM initiative id → its accent hex for the editor `+` badge color dot (warmed cache).
  const initiativeColor = useCallback((id: string): string | undefined => {
    const c = loomInitiativesRef.current.find((i) => i.id === id)?.color;
    return c ? colorHex(c) : undefined;
  }, []);

  // TRELLIS passive marker: the editor reads this lazily to decorate blocks placed in an initiative.
  const resolveInitiativeMarkers = useCallback(() => loomMarkersRef.current, []);
  // The marker's "Open" link navigates to that initiative's detail (`#/initiatives/<name>`).
  const onOpenInitiative = useCallback((name: string) => setView({ kind: 'initiatives', openName: name }), []);

  // Open a right-sidebar panel by its DaySignals name (spec D11 — signals link INTO existing panels).
  const openSignalPanel = useCallback((panel: string, targetId?: string) => {
    // Finances is a full surface, not a right panel — a financial-alert card navigates to it directly.
    if (panel === 'Finances') {
      setView({ kind: 'finances' });
      return;
    }
    // Decisions is a full surface too — the morning "N decisions logged" / revisit-due cards deep-link here.
    // (WEAVE) a `decision-updated` card carries the decisionId in targetId to open that decision directly.
    if (panel === 'decisions') {
      setView({ kind: 'decisions', openId: targetId });
      return;
    }
    // Bills is a full surface (DOCKET D19) — bill-due/bill-paid/reconcile cards carry the billId in
    // targetId so the card lands directly on that bill's detail/forensics.
    if (panel === 'bills') {
      setView({ kind: 'bills', billId: targetId });
      return;
    }
    const map: Record<string, RightViewId> = { Shipments: 'shipping', Today: 'todoist', Agenda: 'agenda', Autonomy: 'autonomy' };
    const id = map[panel];
    if (id) openRightPanel(id);
  }, [openRightPanel]);
  // A DaySignals `create-note`/`open-note` to a journal day routes through openNote via its noteId.
  const openSignalDate = useCallback((dateKey: string) => openNote(dailyJournalNoteId(dateKey)), [openNote]);

  // Server-backed resolvers for block references + embeds (IMPL-07). Undefined when no API is
  // configured, so block refs/embeds simply render their fallback offline.
  const blockServices = useMemo<BlockServices | undefined>(() => {
    if (!API_URL) return undefined;
    return {
      searchBlocks: async (query) => (await api.searchBlocks(query)).map((b) => ({ blockId: b.blockId, text: b.text })),
      resolveBlock: async (blockId) => {
        const b = await api.getBlock(blockId);
        return b ? { noteId: b.noteId, text: b.text } : null;
      },
      resolveEmbed: async (kind, target) => {
        if (kind === 'block') {
          const b = await api.getBlock(target);
          return b ? { noteId: b.noteId, title: '', text: b.text } : null;
        }
        const c = await api.getPageContent(target);
        return c ? { noteId: c.noteId, title: c.title, text: c.text } : null;
      },
      onOpenNote: openNote,
    };
  }, [api, openNote]);

  // Keep the backlink graph current without a reembed worker in local dev: a couple seconds after a
  // note's edits settle (past the sync server's 2s store debounce), ask the server to re-derive its
  // edges from the freshly-written snapshot. Debounced per note; best-effort (ignored when offline).
  const reindexTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pingReindex = useCallback(
    (noteId: string) => {
      if (!API_URL) return;
      const timers = reindexTimers.current;
      const existing = timers.get(noteId);
      if (existing) clearTimeout(existing);
      timers.set(
        noteId,
        setTimeout(() => {
          timers.delete(noteId);
          void api.reindexNote(noteId).catch(() => undefined);
        }, 3000),
      );
    },
    [api],
  );
  pingReindexRef.current = pingReindex;
  useEffect(() => () => reindexTimers.current.forEach((t) => clearTimeout(t)), []);

  // Cmd/Ctrl-K toggles the search palette from anywhere. (DOM event — React's KeyboardEvent type is
  // imported in this module, so reach the global one via WindowEventMap.)
  useEffect(() => {
    const onKey = (e: WindowEventMap['keydown']): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Side-menu keys, chosen to avoid the browser's own shortcuts (the brackets go to Back/Forward — see
  // the next effect). Neither has an editor binding, so both work even while typing.
  //   • Cmd/Ctrl-\        → toggle the LEFT sidebar (Notion-style). Chosen over ⌘B so the editor keeps
  //                         bold; it has no editor binding, so it works even while typing.
  //   • Cmd/Ctrl-Shift-\  → rotate the RIGHT drawer: closed → panel 0 → … → last panel → closed. One
  //                         key cycles the whole side menu, including back to hidden.
  useEffect(() => {
    const onKey = (e: WindowEventMap['keydown']): void => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key !== '\\') return;
      e.preventDefault();
      if (e.shiftKey) {
        // Rotate the right drawer, wrapping past the last panel back to closed (useRightDrawer).
        cycleRightPanel();
        return;
      }
      // Cmd/Ctrl-\ (no Shift): toggle the left sidebar.
      if (window.matchMedia('(max-width: 768px)').matches) setSidebarOpen((o) => !o);
      else setSidebarCollapsed((c) => !c);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycleRightPanel]);

  // History Back/Forward via Cmd/Ctrl-[ and Cmd/Ctrl-]. A plain browser wires these (and the trackpad
  // swipe) to history navigation for free, and the hash-sync listener above turns each popstate back
  // into a surface change. The Tauri macOS shell gives the webview no browser chrome, so nothing there
  // was bound to move through history — hence Back "didn't work" in the desktop app. Drive it ourselves
  // and preventDefault so it's exactly one navigation, identical on web and desktop. (The native
  // two/three-finger swipe is opted into separately in the Rust shell via
  // allowsBackForwardNavigationGestures, which feeds the same popstate path.) The editor doesn't bind
  // Mod-[/] (indent/outdent is Tab/Shift-Tab), so these are safe even while typing.
  useEffect(() => {
    const onKey = (e: WindowEventMap['keydown']): void => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      if (e.key === '[') {
        e.preventDefault();
        window.history.back();
      } else if (e.key === ']') {
        e.preventDefault();
        window.history.forward();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Login gate: social-only. Until there's a real session, show the split-screen login; the
  // Google/Apple buttons run the real Firebase OAuth sign-in (Apple is disabled for now). The
  // Toaster is mounted here too so sign-in errors (e.g. Firebase not configured) are visible.
  if (!session) {
    return (
      <>
        <Login busy={authBusy} onSocial={(provider) => void onSignIn(provider)} />
        <Toaster toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // OAuth consent: a third-party service sent the user here to authorize. Signed in above, so approving
  // binds the resulting token to this account. Rendered instead of the app shell until they choose.
  if (consentRequest) {
    return (
      <>
        <OAuthConsentScreen api={api} request={consentRequest} userEmail={session.user.email ?? undefined} />
        <Toaster toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  return (
    <TeachProvider onTeach={api.teach}>
    <div
      className="lapo-shell"
      data-sidebar={sidebarOpen ? 'open' : 'closed'}
      data-collapsed={sidebarCollapsed ? 'true' : 'false'}
      data-right={rightOpen ? 'open' : 'closed'}
      // macOS desktop only: the window uses an overlay title bar (no native strip), so reserve room for
      // the traffic lights at the sidebar top and give the strip a drag region. See styles.css.
      data-desktop-mac={isTauri() && isMacOS() && !isMobile() ? 'true' : 'false'}
      style={{ ['--lapo-side-w']: `${sidebarWidth}px`, ['--lapo-right-w']: `${rightWidth}px` } as CSSProperties}
    >
      <FileDropOverlay onFiles={onDropFiles} disabled={!uploadFile} />
      <Sidebar
        nav={nav}
        favorites={favorites}
        recents={recents}
        onNav={onNav}
        onSelect={openNote}
        onCreate={onSidebarCreate}
        onToggleStar={onToggleStarById}
        onDismiss={onDismissRecent}
        macDownloadUrl={macDownloadSuggestion()}
        layout={menuLayout}
        onReorder={onMenuReorder}
        onToggleCollapse={onMenuToggleCollapse}
      />
      <div
        className="lapo-side-resize"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        onMouseDown={startSidebarResize}
        onDoubleClick={resetSidebarWidth}
        title="Drag to resize · double-click to reset"
      />
      <button type="button" className="lapo-scrim lapo-scrim-left" aria-label="Close menu" tabIndex={-1} onClick={closeSidebar} />
      <main className="lapo-main">
        <IconBar
          status={status}
          health={healthStatus}
          onHealthClick={() => setHealthPanelOpen(true)}
          onSettings={() => setSettingsOpen(true)}
          onSearch={() => setSearchOpen(true)}
          onToggleSidebar={() =>
            window.matchMedia('(max-width: 768px)').matches
              ? setSidebarOpen((o) => !o)
              : setSidebarCollapsed((c) => !c)
          }
          onToggleRight={toggleRight}
          rightOpen={rightOpen}
          onAssistant={() => setAssistantOpen((o) => !o)}
          assistantOpen={assistantOpen}
          onOutdent={onOutdentActive}
          onIndent={onIndentActive}
        />
        {blockSelActive && (
          <BlockActionBar
            onMoveUp={onBlockMoveUp}
            onMoveDown={onBlockMoveDown}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            onOutdent={onBlockOutdent}
            onIndent={onBlockIndent}
            onToggleNumbered={onBlockNumber}
            numbered={blockNumbered}
            onCopy={onBlockCopy}
            onDelete={onBlockDelete}
            onDone={onBlockDone}
          />
        )}
        {assistantOpen && (
          <>
            <div className="lapo-assistant-scrim" onClick={() => setAssistantOpen(false)} aria-hidden />
            <div ref={assistantPopRef} className="lapo-assistant-pop" role="dialog" aria-label="Assistant">
              <AssistantPanel
                messages={assistantMessages}
                onSend={(prompt) => void onAssist(prompt)}
                onStop={onAssistStop}
                onClear={onAssistClear}
                onClose={() => setAssistantOpen(false)}
                busy={assistantBusy}
                busyStatus={assistantStatus}
                error={assistantError}
                disabledReason={!API_URL ? 'Set VITE_API_URL to use the assistant.' : undefined}
                capabilities={assistantCapabilities}
                onSendImage={(file, prompt) => void onAssistBillImage(file, prompt)}
                onConfirmPay={(billId) => void onConfirmBillPay(billId)}
              />
            </div>
          </>
        )}
        <div className="lapo-main-scroll" ref={setMainRef}>
        <ErrorBoundary label="content-view">
        {view.kind === 'initiatives' ? (
          <LoomView
            api={api}
            disabled={!API_URL}
            onOpenNote={(noteId, blockId) => openNote(noteId, blockId)}
            disabledReason={API_URL ? undefined : 'Set VITE_API_URL to use Initiatives.'}
            openName={view.openName}
          />
        ) : view.kind === 'library' ? (
          <LibraryView
            notes={allNotes}
            categories={categories}
            onOpen={openNote}
            onToggleStar={onToggleStarById}
            onCreate={() => createDocument()}
          />
        ) : view.kind === 'contacts' ? (
          <ContactsList
            contacts={shownContacts}
            recent={recentContacts}
            onMerge={onMergeContacts}
            suggestions={contactSuggestions}
            query={contactQuery}
            onQueryChange={setContactQuery}
            onSync={() => void onSyncContacts()}
            onConfirm={(id) => void onConfirmSuggestion(id)}
            onDismiss={(id) => void onDismissSuggestion(id)}
            loading={contactsLoading}
            syncing={contactsSyncing}
            error={contactsError}
            disabledReason={API_URL ? undefined : 'Set VITE_API_URL to sync contacts.'}
            onOpen={(contactId) => setView({ kind: 'contact', contactId })}
          />
        ) : view.kind === 'contact' ? (
          <ContactDetailView
            api={api}
            contactId={view.contactId}
            onBack={() => setView({ kind: 'contacts' })}
            onOpenDecision={(id) => setView({ kind: 'decisions', openId: id })}
          />
        ) : view.kind === 'integrations' ? (
          <IntegrationsView
            integrations={hubIntegrations}
            onOpen={onOpenIntegration}
            onRun={onRunIntegration}
            running={runningIntegrations}
          />
        ) : view.kind === 'integration' && view.id === 'contact-pictures' ? (
          <HeadshotStudio
            contacts={headshotContacts}
            onBack={() => setView({ kind: 'integrations' })}
            onUpload={(file) => api.uploadBlob(file).then((b) => b.uri)}
            onGenerate={(input) =>
              api.generateHeadshot(input).then((r) => ({ draftId: r.draftId, previewUri: r.previewUri }))
            }
            onApprove={onApproveHeadshot}
            onSignPreview={(uri) => api.signBlob(uri).then((r) => r.url)}
            disabledReason={API_URL ? undefined : 'Set VITE_API_URL to use the headshot studio.'}
          />
        ) : view.kind === 'integration' ? (
          (() => {
            const integration = findIntegration(hubIntegrations, view.id);
            return integration ? (
              <IntegrationDetail
                integration={integration}
                onBack={() => setView({ kind: 'integrations' })}
                onRun={onRunIntegration}
                running={runningIntegrations.has(integration.id)}
                onToggleEnabled={onToggleIntegration}
                connect={integrationConnect}
                settings={integrationSettings}
              />
            ) : (
              <IntegrationsView
                integrations={hubIntegrations}
                onOpen={onOpenIntegration}
                onRun={onRunIntegration}
                running={runningIntegrations}
              />
            );
          })()
        ) : view.kind === 'utilities' ? (
          <UtilitiesView utilities={utilities} onOpen={onOpenUtility} />
        ) : view.kind === 'utility' && view.id === 'gmail-cleanup' ? (
          <UtilityDetail
            utilityId={view.id}
            api={api}
            onBack={() => setView({ kind: 'utilities' })}
            onGrantPermission={onGrantGmailModify}
            onError={(message) => pushToast({ tone: 'error', title: 'Cleanup error', message })}
          />
        ) : view.kind === 'utility' ? (
          <UtilitiesView utilities={utilities} onOpen={onOpenUtility} />
        ) : view.kind === 'subscription' ? (
          <SubscriptionReader api={api} />
        ) : view.kind === 'reddit' ? (
          <RedditMonitorView api={session && API_URL ? api : null} />
        ) : view.kind === 'finances' ? (
          <FinancialDashboard api={api} apiBase={API_URL} />
        ) : view.kind === 'fitness' ? (
          <FitnessView api={api} />
        ) : view.kind === 'kudos' ? (
          <KudosView api={api} />
        ) : view.kind === 'shopping' ? (
          <ShoppingView api={api} />
        ) : view.kind === 'bills' ? (
          <BillsView
            api={api}
            uid={session?.user.uid}
            openBillId={view.billId}
            onOpenBill={(billId) => setView({ kind: 'bills', billId })}
            showDiagnostics={billPayState.config?.showDiagnostics ?? false}
            cardLabelFor={(paymentMethodId) => {
              const cards = billPayState.config?.paymentMethods ?? [];
              const card = cards.find((c) => c.id === paymentMethodId) ?? cards.find((c) => c.isDefault) ?? cards[0];
              return card ? `${card.nickname || card.brand} •${card.last4}` : undefined;
            }}
          />
        ) : view.kind === 'jobs' ? (
          <JobsView
            api={api}
            uid={session?.user.uid}
            openJobId={view.jobId}
            onOpenJob={(jobId) => setView(jobId ? { kind: 'jobs', jobId } : { kind: 'jobs' })}
          />
        ) : view.kind === 'decisions' ? (
          <DecisionLedgerView
            api={api}
            initialDecisionId={view.openId}
            onOpenNote={(noteId, blockId) => setView({ kind: 'doc', noteId, scrollToBlockId: blockId })}
            onOpenInitiative={() => setView({ kind: 'initiatives' })}
          />
        ) : view.kind === 'ai-coaching' ? (
          <AICoachingView
            module={view.module}
            onNavModule={(m) => setView({ kind: 'ai-coaching', module: m })}
            api={session && API_URL ? api : null}
            writingStyle={writingStyle}
            onCommitWritingStyle={session && API_URL ? onCommitWritingStyle : undefined}
            workingHours={workingHours}
            onCommitWorkingHours={session && API_URL ? onCommitWorkingHours : undefined}
            quillVoice={quillVoice}
            onChangeQuillVoice={session && API_URL ? onChangeQuillVoice : undefined}
            onBootstrapWritingStyle={session && API_URL ? onBootstrapWritingStyle : undefined}
            autonomyApi={session && API_URL ? api : null}
            today={localDateKey(new Date())}
            axisEnabled={axisEnabled}
            onToggleAxis={onToggleAxis}
            actionDefaults={actionDefaults}
            onCommitActionDefaults={onCommitActionDefaults}
            ingestTriage={ingestTriageCfg}
            onCommitIngestTriage={onCommitIngestTriage}
          />
        ) : view.kind === 'doc' ? (
<>
            {docBanner && (
              <div
                className="lapo-doc-banner-full"
                style={{
                  height: `${docBanner.height ?? 220}px`,
                  backgroundImage: `url("${docBanner.url}")`,
                  backgroundPosition: `${docBanner.alignX ?? '50%'} ${docBanner.align ?? '50%'}`,
                  backgroundSize: `${docBanner.zoom ?? 100}%`,
                }}
              />
            )}
            <div className="lapo-journal">
              <section className="lapo-day">
                <DocEditor
                  key={view.noteId}
                  noteId={view.noteId}
                  initialTitle={view.initialTitle}
                  scrollToBlockId={view.scrollToBlockId}
                  candidateSource={candidateSource}
                  onDocNames={onDocNames}
                  onNavigate={onNavigate}
                  onInitiativeTagDelete={onInitiativeTagDelete}
                  initiativeColor={initiativeColor}
                  resolveInitiativeMarkers={resolveInitiativeMarkers}
                  onOpenInitiative={onOpenInitiative}
                  block={blockServices}
                  onAliases={onAliases}
                  onDiscardEmpty={onDiscardEmpty}
                  onStatus={setStatus}
                  userId={session?.user.uid}
                  getToken={session ? getSyncToken : undefined}
                  onBanner={onDocBanner}
                  starred={allNotes.find((n) => n.noteId === view.noteId)?.starred ?? false}
                  onToggleStar={() => onToggleStarById(view.noteId)}
                  onReindex={() => pingReindexRef.current(view.noteId)}
                  onDelete={() => deleteDocument(view.noteId)}
                  onActiveEditor={setActiveEditor}
                />
                <LinkedReferences
                  api={api}
                  noteId={view.noteId}
                  name={allNotes.find((n) => n.noteId === view.noteId)?.title ?? view.initialTitle}
                  onOpenNote={openNote}
                  onNavigate={onNavigate}
                />
              </section>
            </div>
          </>
        ) : (
          <div className="lapo-journal">
            {days.map((d) => {
              // Windowing (F6): far-off-screen days render a height-preserving placeholder instead
              // of the live editor + signals; see useDayWindow for the full contract.
              const win = dayWindowSection(d.noteId);
              return (
              <section key={d.noteId} className="lapo-day" ref={win.sectionRef}>
                <div className="lapo-day-head">
                  <h1 className="lapo-day-title">{d.title}</h1>
                  <button
                    type="button"
                    className={`lapo-day-star${allNotes.find((n) => n.noteId === d.noteId)?.starred ? ' lapo-starred' : ''}`}
                    title="Toggle favorite"
                    aria-label="Toggle favorite"
                    onClick={() => onToggleStarById(d.noteId)}
                  >
                    <MaterialIcon name="star" size={18} />
                  </button>
                </div>
                {win.live ? (
                <div
                  className="lapo-day-body"
                  ref={win.bodyRef}
                  style={win.holdHeight != null ? { minHeight: win.holdHeight } : undefined}
                >
                <DayEditor
                  noteId={d.noteId}
                  placeholder={d.isToday ? "Start writing today's journal…" : undefined}
                  onStatus={setStatus}
                  candidateSource={candidateSource}
                  onDocNames={onDocNames}
                  onNavigate={onNavigate}
                  onInitiativeTagDelete={onInitiativeTagDelete}
                  initiativeColor={initiativeColor}
                  resolveInitiativeMarkers={resolveInitiativeMarkers}
                  onOpenInitiative={onOpenInitiative}
                  onEventNote={(eventId) => void openEventNote(eventId, 'Meeting notes')}
                  block={blockServices}
                  userId={session?.user.uid}
                  getToken={session ? getSyncToken : undefined}
                  onActiveEditor={setActiveEditor}
                  onBlockSelection={onBlockSelection}
                  onImportInstagram={session && API_URL ? onEditorImportInstagram : undefined}
                  onRescanInstagram={session && API_URL ? onEditorRescanInstagram : undefined}
                  onResolveLink={session && API_URL ? onEditorResolveLink : undefined}
                  onSummarizePodcast={session && API_URL ? onEditorSummarizePodcast : undefined}
                />
                <DaySignals
                  dateKey={d.noteId.replace('journal-', '')}
                  today={localDateKey(new Date())}
                  api={API_URL ? api : null}
                  onOpenNote={openNote}
                  onOpenDate={openSignalDate}
                  onOpenPanel={openSignalPanel}
                />
                </div>
                ) : (
                <div className="lapo-day-body" style={{ height: win.height ?? 0 }} aria-hidden />
                )}
              </section>
              );
            })}
          </div>
        )}
        </ErrorBoundary>
        </div>
      </main>
      <button
        type="button"
        className="lapo-scrim lapo-scrim-right"
        aria-label="Close panel"
        tabIndex={-1}
        onClick={closeRight}
      />
      <aside className="lapo-rightbar" data-testid="rightbar" aria-label={RIGHT_VIEWS[rightViewIndex]?.title ?? 'Panel'}>
        <div
          className="lapo-rd-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          onMouseDown={startRightResize}
          onDoubleClick={resetRightWidth}
          title="Drag to resize · double-click to reset"
        />
        <div className="lapo-rightbar-head">
          {RIGHT_VIEWS.length > 1 && (
            <div className="lapo-rd-dots" role="tablist" aria-label="Panels">
              {RIGHT_VIEWS.map((v, i) => (
                <button
                  key={v.id}
                  type="button"
                  role="tab"
                  aria-selected={i === rightViewIndex}
                  className={`lapo-rd-dot${i === rightViewIndex ? ' lapo-rd-dot-active' : ''}`}
                  title={v.title}
                  aria-label={v.title}
                  onClick={() => setRightViewIndex(i)}
                >
                  <MaterialIcon name={v.icon} size={16} />
                </button>
              ))}
            </div>
          )}
          <button type="button" className="lapo-icon-btn" title="Close panel (⌘[)" aria-label="Close panel" onClick={closeRight}>
            <MaterialIcon name="close" size={20} />
          </button>
        </div>
        <div className="lapo-rd-body">
          {RIGHT_VIEWS[rightViewIndex]?.id === 'autonomy' ? (
            <AutonomyPanel
              api={session && API_URL ? api : null}
              today={localDateKey(new Date())}
              enabled={axisEnabled}
              onToggleEnabled={onToggleAxis}
              actionDefaults={actionDefaults}
              onCommitActionDefaults={onCommitActionDefaults}
              ingestTriage={ingestTriageCfg}
              onCommitIngestTriage={onCommitIngestTriage}
            />
          ) : RIGHT_VIEWS[rightViewIndex]?.id === 'agenda' ? (
            <AgendaPanel
              loading={agendaDayLoading}
              error={agendaDayError}
              day={agendaDay}
              dateKey={agendaDateKey}
              isToday={agendaDateKey === localDateKey(new Date())}
              onRefresh={onAgendaRefresh}
              onPrevDay={onAgendaPrevDay}
              onNextDay={onAgendaNextDay}
              onToday={onAgendaToday}
              onOpenEventNote={onOpenAgendaEventNote}
            />
          ) : RIGHT_VIEWS[rightViewIndex]?.id === 'shipping' ? (
            <ShipmentsView
              shipments={shipments}
              tracked={tracked}
              draft={shipDraft}
              onBuy={(easypostId, rateId) => void onBuyLabel(easypostId, rateId)}
              onClearDraft={() => setShipDraft(null)}
              onRefresh={(id) => void onRefreshShipment(id)}
              onEditShipment={(id, patch) => void onEditShipment(id, patch)}
              onArchiveShipment={(id, archived) => void onArchiveShipment(id, archived)}
              onSync={() => void onSyncShipments()}
              onTrack={(trackingCode, carrier) => void onTrackPackage(trackingCode, carrier)}
              onRefreshTracked={(id) => void onRefreshTracked(id)}
              onUntrack={(id) => void onUntrack(id)}
              onArchiveTracked={(id, archived) => void onArchiveTracked(id, archived)}
              onEditTracked={(id, patch) => void onEditTracked(id, patch)}
              onScanEmail={() => void onScanEmail()}
              scanningEmail={scanningEmail}
              trackingBusy={trackingBusy}
              refreshingTrackedId={refreshingTrackedId}
              loading={shipmentsLoading}
              refreshing={shipmentsLoading && shipments.length > 0}
              buying={shipBuying}
              syncing={shipSyncing}
              refreshingId={shipRefreshingId}
              error={shipError}
              disabledReason={
                !API_URL
                  ? 'Set VITE_API_URL to use shipping.'
                  : !easypost
                    ? 'Connect EasyPost in Settings → Integrations to create labels.'
                    : undefined
              }
            />
          ) : RIGHT_VIEWS[rightViewIndex]?.id === 'shopping' ? (
            <ShoppingDrawer
              api={api}
              onOpenShopping={() => setView({ kind: 'shopping' })}
              disabledReason={!API_URL ? 'Set VITE_API_URL to use Amazon shopping.' : undefined}
            />
          ) : (
            <TodoistPanel
              connected={!!todoist}
              loading={todoistLoading}
              error={todoistError}
              data={todoistToday}
              needsReconnect={todoistReconnect}
              completing={completingTasks}
              pendingTaskId={pendingUndo?.taskId}
              undoContent={pendingUndo?.content ?? null}
              onRefresh={onTodoistRefresh}
              onComplete={onCompleteTask}
              onUndo={onUndoComplete}
              onDismissUndo={onDismissUndo}
              onConnect={onTodoistConnect}
              onReconnect={onTodoistReconnect}
            />
          )}
        </div>
      </aside>
      {isAdmin && healthPanelOpen && (
        <LazyBoundary>
          <LazyHealthPanel
            open={healthPanelOpen}
            onClose={() => setHealthPanelOpen(false)}
            snapshot={health.snapshot}
            events={healthEvents}
            loadingEvents={healthEventsLoading}
            onRefresh={loadHealthEvents}
            fetchLogs={(severity) =>
              api
                .getAdminLogs({ severity })
                .then((r) => r.entries)
                .catch(() => null)
            }
          />
        </LazyBoundary>
      )}
      {settingsEverOpened && <SettingsModal
        open={settingsOpen}
        settings={settings}
        onChange={updateSettings}
        onCommitDisplayName={onCommitDisplayName}
        biography={serverBiography}
        onCommitBiography={session && API_URL ? onCommitBiography : undefined}
        contact={contact}
        onCommitContact={session && API_URL ? onCommitContact : undefined}
        googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
        accountIdentities={accountIdentities}
        onCommitAccountIdentity={session && API_URL ? onCommitAccountIdentity : undefined}
        onClose={() => setSettingsOpen(false)}
        updates={desktopEnabled ? desktopUpdater.state : undefined}
        onCheckForUpdates={desktopUpdater.check}
        onRelaunch={desktopUpdater.relaunch}
        integrations={integrations}
        onConnectGoogle={onConnectGoogle}
        onConnectClaude={onConnectClaude}
        onDisconnect={onDisconnect}
        telegram={
          session && API_URL
            ? {
                status: () => api.telegramStatus(),
                connect: () => api.telegramLinkCode(),
                disconnect: () => api.telegramUnlink().then(() => undefined),
              }
            : undefined
        }
        account={account}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        toolbar={toolbarCfg.config}
        onChangeToolbar={toolbarCfg.update}
        automations={session && API_URL ? { catalog: engineCfg.catalog, config: engineCfg.config } : undefined}
        onChangeEngineConfig={session && API_URL ? engineCfg.update : undefined}
        triage={session && API_URL ? api : undefined}
        onResetMenuLayout={session && API_URL ? onMenuReset : undefined}
      />}
      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.title ?? ''}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel ?? 'Disconnect'}
        danger
        onConfirm={() => {
          confirm?.onConfirm();
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
      <SearchModal
        open={searchOpen}
        notes={allNotes}
        onClose={() => setSearchOpen(false)}
        onOpen={openNote}
        onCreate={(title) => createDocument(title)}
        searchContent={session ? api.searchContent : undefined}
        localContent={localContentIndex ? (q) => localContentIndex.search(q) : undefined}
        currentNoteId={view.kind === 'doc' ? view.noteId : undefined}
        searchInNote={localContentIndex ? (noteId, q) => localContentIndex.searchWithin(noteId, q) : undefined}
        searchPalette={session ? api.searchPalette : undefined}
        onOpenContact={(contactId) => {
          setSearchOpen(false);
          setView({ kind: 'contact', contactId });
        }}
        onPeek={peekNote}
      />
      <Toaster toasts={toasts} onDismiss={dismissToast} />
      {view.kind !== 'fitness' ? <GlobalTeachButton {...producerForView(view.kind)} /> : null}
    </div>
    </TeachProvider>
  );
}
